import { useState, useEffect, useRef, useCallback } from "react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from "recharts";
import html2canvas from "html2canvas";
import "./App.css";

// 是否為手機裝置（環境固定值，不會隨 render 變動，模組層級只算一次）
// Windows 11 的 Edge/Chrome 桌面版也會回報支援 navigator.canShare 檔案分享，
// 只看 canShare 會誤跳系統分享視窗，所以圖卡下載要另外判斷是不是真的手機
const IS_MOBILE_DEVICE = navigator.userAgentData
  ? navigator.userAgentData.mobile
  : /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

// 自動帶 token 的 fetch wrapper
async function authFetch(url, options = {}) {
  const token = localStorage.getItem("auth_token");
  const headers = { ...(options.headers || {}) };
  if (token) {
    headers["Authorization"] = `Bearer ${token}`;
  }
  const resp = await fetch(url, { ...options, headers });
  if (resp.status === 401) {
    // Token 失效，清掉並跳回登入
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    window.location.reload();
  }
  return resp;
}

// products_catalog.status（active/removed）跟 price_history.in_stock 是兩個獨立資料源；
// 下架（removed）要蓋過庫存波動，因為商品都沒了，有貨/缺貨已經沒意義
function priceStockLabel(status, inStock) {
  if (status === "removed") return "下架";
  return inStock ? "有貨" : "缺貨";
}

// 漲跌價發生的那個點用大一點的紅框圓點標出來，取代原本「該點軸標籤變長」的做法——
// 混用不同長度的軸標籤會讓 recharts 的類別軸重疊偵測誤判、直接吃掉某些刻度文字
function changePointDot(props) {
  const { cx, cy, payload, index } = props;
  if (payload.changed && index !== 0) {
    return <circle key={`dot-${index}`} cx={cx} cy={cy} r={6} fill="#dc2626" stroke="#fff" strokeWidth={2} />;
  }
  return <circle key={`dot-${index}`} cx={cx} cy={cy} r={3} fill="#0056b3" />;
}

// price_history 同一天可能有好幾筆記錄（純庫存切換也會多寫一筆，價格沒變），
// 圖表只在乎價格，所以每天只留一個代表點：當天價格都沒變就用第一筆（開盤價，
// 通常是 00:10 那次基準檢查），當天價格有變就用最後一筆（收盤價，變動後的結果）
function groupHistoryByDay(hist) {
  const byDate = new Map();
  for (const h of hist) {
    const dateKey = h.time.substring(0, 10);
    if (!byDate.has(dateKey)) byDate.set(dateKey, []);
    byDate.get(dateKey).push(h);
  }
  const daily = [];
  for (const rows of byDate.values()) {
    const firstPrice = rows[0].price;
    const changedWithinDay = rows.some(r => r.price !== firstPrice);
    daily.push(changedWithinDay ? rows[rows.length - 1] : rows[0]);
  }
  return daily;
}

// 製表時間（生成當下），不是最後一次 price_history 檢查時間——顯示的價格/庫存
// 已經是目前已知最新值，用檢查時間反而容易讓人誤以為資料是舊的
function fmtNow() {
  const d = new Date();
  const pad = n => String(n).padStart(2, "0");
  return `${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

const TABS = [
  ["dashboard", "📊", "總覽"],
  ["catalog", "🔍", "全站商品"],
  ["events", "📰", "最新動態"],
  ["mylist", "📦", "我的清單"],
  ["notifications", "🔔", "通知紀錄"],
  ["charts", "📊", "價格走勢"],
  ["settings", "⚙️", "我的設定"],
  ["users", "👥", "用戶管理"],
  ["invites", "🎟️", "邀請碼管理"],
  ["system", "⚙️", "系統管理"],
];

function LoginPage({ onLogin }) {
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    window.onTelegramAuth = async (user) => {
      setLoading(true);
      setError("");
      try {
        const r = await authFetch("/api/auth/telegram-login", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(user)
        });
        const d = await r.json();
        if (d.token) {
          localStorage.setItem("auth_token", d.token);
          localStorage.setItem("auth_user", JSON.stringify(d));
          onLogin(d);
        } else {
          setError(d.error || "登入失敗");
        }
      } catch (e) {
        setError("連線錯誤");
      } finally {
        setLoading(false);
      }
    };
    const script = document.createElement("script");
    script.src = "https://telegram.org/js/telegram-widget.js?22";
    script.async = true;
    script.setAttribute("data-telegram-login", "CoTrackbot");
    script.setAttribute("data-size", "large");
    script.setAttribute("data-onauth", "onTelegramAuth(user)");
    script.setAttribute("data-request-access", "write");
    const container = document.getElementById("telegram-login-container");
    if (container) { container.innerHTML = ""; container.appendChild(script); }
    return () => { delete window.onTelegramAuth; };
  }, [onLogin]);

  return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "linear-gradient(145deg, #2563eb 0%, #1141a8 100%)" }}>
      <div style={{ background: "white", borderRadius: 20, padding: "40px 36px", width: 400, boxShadow: "0 24px 80px rgba(0,0,0,0.35)", textAlign: "center" }}>

        <div style={{ marginBottom: 10 }}><img src="/logo192.png" alt="CoTrackBot" style={{ width: 72, height: 72, borderRadius: 16 }} /></div>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: "#111827", margin: "0 0 4px" }}>CoTrackBot</h1>
        <p style={{ fontSize: 13, color: "#6b7280", margin: "0 0 4px" }}>價格及庫存監控</p>

        <div style={{ margin: "24px 0 8px" }}>
          <QRLoginBlock onLogin={onLogin} />
        </div>

        {error && (
          <div style={{ background: "#fef2f2", border: "1px solid #fecaca", color: "#b91c1c", padding: "10px 14px", borderRadius: 8, marginBottom: 12, fontSize: 13 }}>❌ {error}</div>
        )}

        {loading
          ? <div style={{ padding: "12px 0", color: "#6b7280", fontSize: 13 }}>登入中...</div>
          : <div style={{ display: "flex", justifyContent: "center" }}><div id="telegram-login-container" /></div>
        }

        <p style={{ marginTop: 20, fontSize: 12, color: "#d1d5db", marginBottom: 0 }}>
          尚未註冊？請先用邀請碼加入 @CoTrackbot
        </p>
      </div>
    </div>
  );
}

function QRLoginBlock({ onLogin }) {
  const [qrToken, setQrToken] = useState(null);
  const [status, setStatus] = useState("loading");
  const [secondsLeft, setSecondsLeft] = useState(300);
  const pollRef = useRef(null);
  const countdownRef = useRef(null);

  const initQR = useCallback(() => {
    setStatus("loading");
    setQrToken(null);
    setSecondsLeft(300);
    if (countdownRef.current) clearInterval(countdownRef.current);
    fetch("/api/auth/qr-init", { method: "POST" })
      .then(r => r.json())
      .then(d => { setQrToken(d.token); setStatus("ready"); })
      .catch(() => setStatus("error"));
  }, []);

  useEffect(() => {
    initQR();
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [initQR]);

  useEffect(() => {
    if (countdownRef.current) clearInterval(countdownRef.current);
    if (status !== "ready") return;
    countdownRef.current = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) {
          clearInterval(countdownRef.current);
          setStatus("expired");
          return 0;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(countdownRef.current);
  }, [status]);

  useEffect(() => {
    if (pollRef.current) clearInterval(pollRef.current);
    if (status !== "ready" || !qrToken) return;
    pollRef.current = setInterval(() => {
      fetch(`/api/auth/qr-poll/${qrToken}`)
        .then(r => r.json())
        .then(d => {
          if (d.status === "confirmed") {
            clearInterval(pollRef.current);
            localStorage.setItem("auth_token", d.token);
            localStorage.setItem("auth_user", JSON.stringify(d));
            onLogin(d);
          } else if (d.status === "expired") {
            clearInterval(pollRef.current);
            setStatus("expired");
          }
        })
        .catch(() => {});
    }, 2000);
    return () => clearInterval(pollRef.current);
  }, [status, qrToken, onLogin]);

  const BOT = "CoTrackbot";
  const qrUrl = qrToken
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`https://t.me/${BOT}?start=${qrToken}`)}&bgcolor=ffffff&color=111827&margin=10`
    : null;

  if (status === "loading") return <div style={{ color: "#9ca3af", fontSize: 13, padding: "40px 0" }}>產生 QR Code...</div>;
  if (status === "error") return <div style={{ color: "#dc2626", fontSize: 13, padding: "20px 0" }}>❌ 無法產生 QR Code</div>;
  if (status === "expired") return (
    <div style={{ padding: "12px 0" }}>
      <div style={{ color: "#9ca3af", fontSize: 13, marginBottom: 12 }}>QR Code 已過期</div>
      <button onClick={initQR} style={{ padding: "8px 20px", background: "#2563eb", color: "white", border: "none", borderRadius: 8, cursor: "pointer", fontSize: 13, fontWeight: 600 }}>重新產生</button>
    </div>
  );

  return (
    <div>
      <div style={{ display: "inline-block", padding: 10, borderRadius: 12, border: "2px solid #f3f4f6", boxShadow: "0 4px 16px rgba(0,0,0,0.08)" }}>
        <img src={qrUrl} alt="QR Code" style={{ display: "block", borderRadius: 6, width: 200, height: 200 }} />
      </div>
      <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 8 }}>手機掃描自動登入</div>
      <div style={{ fontSize: 13, fontWeight: 600, marginTop: 4, marginBottom: 4, color: secondsLeft <= 60 ? "#dc2626" : secondsLeft <= 120 ? "#d97706" : "#6b7280" }}>
        {`${Math.floor(secondsLeft / 60)}:${String(secondsLeft % 60).padStart(2, "0")} 後失效`}
      </div>
    </div>
  );
}

function App() {
  const [authUser, setAuthUser] = useState(() => {
    try {
      const token = localStorage.getItem("auth_token");
      const user = localStorage.getItem("auth_user");
      if (token && user) return JSON.parse(user);
    } catch (e) {}
    return null;
  });
  const [tab, setTabRaw] = useState(() => {
    const path = window.location.pathname.replace("/", "").split("?")[0];
    if (!path) return "dashboard";
    const valid = TABS.map(t => t[0]);
    if (!valid.includes(path)) { window.location.replace("/404.html"); return "dashboard"; }
    return path;
  });
  const setTab = (newTab, search) => {
    setTabRaw(newTab);
    window.history.pushState({}, "", "/" + newTab + (search ? "?" + search : ""));
  };

  // 監聽瀏覽器前進/後退按鈕
  useEffect(() => {
    const onPopState = () => {
      const path = window.location.pathname.replace("/", "");
      const valid = TABS.map(t => t[0]);
      if (!path) setTabRaw("dashboard");
      else if (valid.includes(path)) setTabRaw(path);
      else window.location.replace("/404.html");
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mylistFilter, setMylistFilter] = useState("all");
  const [catalogOnSale, setCatalogOnSale] = useState(false);
  const [catalogOnCoupon, setCatalogOnCoupon] = useState(false);
  const [svcStatus, setSvcStatus] = useState(null);
  useEffect(() => {
    if (!authUser) return;
    const fetchSvc = () => authFetch("/api/status").then(r => r.json()).then(setSvcStatus).catch(() => {});
    fetchSvc();
    const t = setInterval(fetchSvc, 60000);
    return () => clearInterval(t);
  }, [authUser]);

  const goMyList = (filter) => {
    setMylistFilter(filter);
    setTab("mylist");
  };

  const handleLogout = () => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_user");
    setAuthUser(null);
  };

  if (!authUser) {
    return <LoginPage onLogin={(u) => setAuthUser(u)} />;
  }

  return (
    <div className="layout">
      {sidebarOpen && <div className="sidebar-backdrop" onClick={() => setSidebarOpen(false)} />}
      <aside className={`sidebar ${sidebarOpen ? "mobile-open" : ""}`}>
        <div className="brand">
          <img src="/logo192.png" alt="logo" style={{ width: 44, height: 44, borderRadius: 8 }} />
          <div>
            <div className="brand-name">CoTrackBot</div>
            <div className="brand-sub">價格及庫存監控</div>
          </div>
        </div>
        <nav>
          {TABS.filter(([id]) => {
            // non-admin 不顯示 users、invites 和 system
            if (authUser?.role !== "admin" && (id === "users" || id === "invites" || id === "system")) return false;
            return true;
          }).map(([id, icon, label]) => (
            <button
              key={id}
              className={`nav-btn ${tab === id ? "active" : ""}`}
              onClick={() => { setTab(id); setSidebarOpen(false); }}
            >
              <span className="nav-icon">{icon}</span>
              <span>{label}</span>
            </button>
          ))}
        </nav>
        <div className="sidebar-footer" style={{ flexDirection: "column", alignItems: "flex-start", gap: 3 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <span className="dot" style={{ background: svcStatus?.bot_ok === false ? "#ef4444" : "#22c55e",
              boxShadow: `0 0 0 3px ${svcStatus?.bot_ok === false ? "rgba(239,68,68,0.2)" : "rgba(34,197,94,0.2)"}` }}></span>
            <span style={{ fontWeight: 500, color: svcStatus?.bot_ok === false ? "#ef4444" : "#374151" }}>
              {svcStatus == null ? "檢查中…" : svcStatus.bot_ok ? "服務正常" : "服務異常"}
            </span>
          </div>
          {svcStatus?.stock_last && (
            <div style={{ fontSize: 10, color: "#9ca3af", paddingLeft: 14 }}>庫存更新 {svcStatus.stock_last}</div>
          )}
          {svcStatus?.sync_last && (
            <div style={{ fontSize: 10, color: "#9ca3af", paddingLeft: 14 }}>商品同步 {svcStatus.sync_last}</div>
          )}
        </div>
        <div style={{ padding: "16px 20px", borderTop: "1px solid #f3f4f6", marginTop: "auto" }}>
          <div style={{ fontSize: 13, color: "#6b7280", marginBottom: 8 }}>
            {authUser?.role === "admin" ? "👑" : "👤"} {authUser?.name}
          </div>
          <button
            onClick={handleLogout}
            style={{ width: "100%", padding: "8px", background: "#f3f4f6", border: "none",
              borderRadius: 6, fontSize: 13, color: "#374151", cursor: "pointer" }}
          >
            登出
          </button>
        </div>
      </aside>
      <main className="main">
        <button className="mobile-menu-btn" onClick={() => setSidebarOpen(true)} aria-label="選單">☰</button>
        {tab === "dashboard" && (
          <Dashboard
            onGoMyList={goMyList}
            onGoUsers={() => setTab("users")}
            onGoCatalogSale={() => { setCatalogOnSale(true); setCatalogOnCoupon(false); setTab("catalog"); }}
            onGoCatalogCoupon={() => { setCatalogOnCoupon(true); setCatalogOnSale(false); setTab("catalog"); }}
            onGoCatalog={() => { setCatalogOnSale(false); setCatalogOnCoupon(false); setTab("catalog"); }}
            onGoCatalogView={(v) => { window._catalogView = v; setCatalogOnSale(false); setCatalogOnCoupon(false); setTab("catalog"); }}
            onGoEvents={(type) => { window.history.pushState({}, "", "/events?type=" + type); setTabRaw("events"); }}
            onGoSettings={() => setTab("settings")}
          />
        )}
        {tab === "catalog" && (
          <Catalog
            onSale={catalogOnSale}
            setOnSale={setCatalogOnSale}
            onCoupon={catalogOnCoupon}
            setOnCoupon={setCatalogOnCoupon}
          />
        )}
        {tab === "newproducts" && <NewProducts />}
        {tab === "discounts" && <PriceChanges type="down" title="最新降價" />}
        {tab === "raises" && <PriceChanges type="up" title="最新漲價" />}
        {tab === "lowstock" && <LowStock />}
        {tab === "restocks" && <StockChanges type="in" title="最新補貨" />}
        {tab === "clearance" && <Clearance />}
        {tab === "removed" && <RemovedProducts />}
        {tab === "mylist" && <MyList initialFilter={mylistFilter} />}
        {tab === "notifications" && <NotificationsPage />}
        {tab === "charts" && <ChartsPage />}
        {tab === "outofstock" && <OutOfStockProducts />}
        {tab === "users" && <UsersPage onSelectUser={(uid) => { localStorage.setItem("selected_user", uid); setTab("mylist"); }} />}
        {tab === "invites" && <InviteCodesPage />}
        {tab === "events" && <EventsPage />}
        {tab === "settings" && <SettingsPage />}
        {tab === "system" && <SystemPage />}
      </main>
    </div>
  );
}

function PasswordCell({ userId }) {
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const fetchPassword = () => {
    setLoading(true);
    authFetch(`/api/users/${userId}/password`)
      .then(r => r.json())
      .then(d => setPassword(d.login_password || "未設定"))
      .catch(() => setPassword("錯誤"))
      .finally(() => setLoading(false));
  };

  const regenerate = () => {
    if (!window.confirm("確定要重新生成密碼嗎？舊密碼會立即失效。")) return;
    setLoading(true);
    authFetch(`/api/users/${userId}/password`, { method: "POST" })
      .then(r => r.json())
      .then(d => setPassword(d.login_password || "錯誤"))
      .catch(() => setPassword("錯誤"))
      .finally(() => setLoading(false));
  };

  if (!password) {
    return (
      <button
        onClick={fetchPassword}
        disabled={loading}
        style={{ padding: "4px 10px", background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", borderRadius: 4, cursor: "pointer", fontSize: 12 }}
      >
        👁 查看
      </button>
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
      <span style={{ fontFamily: "monospace", fontSize: 13, fontWeight: 600, color: "#111827" }}>{password}</span>
      <button
        onClick={regenerate}
        disabled={loading}
        style={{ padding: "2px 8px", background: "#fef3c7", color: "#92400e", border: "1px solid #fde68a", borderRadius: 4, cursor: "pointer", fontSize: 10 }}
      >
        🔄 重設
      </button>
    </div>
  );
}



function ToggleSwitch({ on, onChange, disabled, loading }) {
  return (
    <div
      onClick={!disabled && !loading ? onChange : undefined}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        cursor: disabled || loading ? "default" : "pointer",
        userSelect: "none",
      }}
    >
      <div style={{
        width: 40, height: 22, borderRadius: 11,
        background: loading ? "#d1d5db" : on ? "#16a34a" : "#d1d5db",
        position: "relative", transition: "background 0.2s",
        opacity: disabled ? 0.6 : 1,
        flexShrink: 0,
      }}>
        <div style={{
          width: 18, height: 18, borderRadius: "50%",
          background: "white",
          position: "absolute", top: 2,
          left: loading ? 11 : on ? 20 : 2,
          transition: "left 0.15s",
          boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
        }} />
      </div>
      <span style={{ fontSize: 11, color: loading ? "#9ca3af" : on ? "#16a34a" : "#9ca3af", minWidth: 14 }}>
        {loading ? "…" : on ? "開" : "關"}
      </span>
    </div>
  );
}


function KeywordLimitEditor({ user, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(String(user.keyword_alert_limit ?? 3));

  const save = () => {
    const num = parseInt(val);
    if (isNaN(num) || num < 0 || num > 999) {
      alert("上限需為 0-999 的整數");
      return;
    }
    authFetch(`/api/users/${user.user_id}/keyword-alert-limit`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ limit: num })
    })
      .then(r => r.json())
      .then(d => {
        if (d.error) alert(d.error);
        else { onUpdate(num); setEditing(false); }
      });
  };

  const count = user.keyword_alert_count ?? 0;
  const limit = user.keyword_alert_limit ?? 3;

  if (editing) {
    return (
      <div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
        <input
          type="number" value={val} onChange={e => setVal(e.target.value)}
          style={{ width: 50, padding: "2px 6px", border: "1px solid #d1d5db", borderRadius: 4 }}
          min="0" max="999"
        />
        <button onClick={save} style={{ padding: "2px 8px", background: "#16a34a", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12 }}>✓</button>
        <button onClick={() => { setVal(String(limit)); setEditing(false); }} style={{ padding: "2px 8px", background: "#9ca3af", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12 }}>✕</button>
      </div>
    );
  }
  
  return (
    <button
      onClick={() => setEditing(true)}
      style={{ padding: "4px 10px", background: "#f3f4f6", color: "#374151", border: "1px solid #d1d5db", borderRadius: 4, cursor: "pointer", fontSize: 12, whiteSpace: "nowrap" }}
    >
      {count}/{limit} ✏️
    </button>
  );
}



function NoteEditor({ user, onUpdate }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(user.note || "");
  const [saving, setSaving] = useState(false);

  const save = () => {
    const trimmed = val.trim();
    if (trimmed === (user.note || "")) { setEditing(false); return; }
    setSaving(true);
    authFetch(`/api/users/${user.user_id}/note`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ note: trimmed }),
    }).then(r => r.json()).then(d => {
      if (!d.error) { onUpdate(trimmed); setEditing(false); }
      else alert(d.error || "儲存失敗");
    }).catch(() => alert("連線失敗")).finally(() => setSaving(false));
  };

  if (editing) {
    return (
      <input
        autoFocus
        value={val}
        onChange={e => setVal(e.target.value)}
        onBlur={save}
        onKeyDown={e => {
          if (e.key === "Enter") { e.preventDefault(); save(); }
          if (e.key === "Escape") { setVal(user.note || ""); setEditing(false); }
        }}
        style={{ width: 130, fontSize: 11, padding: "2px 5px", border: "1px solid #93c5fd", borderRadius: 3, outline: "none" }}
        placeholder="輸入備註..."
        disabled={saving}
      />
    );
  }
  const hint = user.invite_label || "";
  return (
    <div
      onClick={() => setEditing(true)}
      title={hint && !val ? `邀請碼備註：${hint}（點擊填入正式備註）` : "點擊編輯備註"}
      style={{
        fontSize: 11, marginTop: 3, cursor: "pointer", maxWidth: 150,
        color: val ? "#3b82f6" : hint ? "#f59e0b" : "#d1d5db",
        overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
      }}
    >
      {val || (hint ? `🔖 ${hint}` : "＋備註")}
    </div>
  );
}

function UsersPage({ onSelectUser }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [togglingId, setTogglingId] = useState("");
  const [removingId, setRemovingId] = useState("");
  const [usersPage, setUsersPage] = useState(1);
  const USERS_PAGE_SIZE = 50;

  const loadUsers = () => {
    setLoading(true);
    authFetch("/api/users")
      .then(r => r.json())
      .then(d => setUsers(Array.isArray(d) ? d : []))
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadUsers(); }, []);

  const totalProducts = users.reduce((sum, u) => sum + (u.product_count || 0), 0);
  const ADMIN_ID = "777157753";

  const toggleInvite = (userId, current) => {
    setTogglingId(userId);
    authFetch(`/api/users/${userId}/permissions`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ can_invite: !current })
    })
      .then(r => r.json())
      .then(d => {
        if (d.message) {
          setUsers(prev => prev.map(u =>
            String(u.user_id) === String(userId)
              ? { ...u, can_invite: d.can_invite }
              : u
          ));
        } else {
          alert(d.error || "更新失敗");
        }
      })
      .catch(() => alert("連線失敗"))
      .finally(() => setTogglingId(""));
  };

  const handleRemove = (userId, username) => {
    if (!window.confirm(`確定移除使用者「${username}」（${userId}）嗎？\n\n此操作不可復原。`)) return;
    setRemovingId(String(userId));
    authFetch(`/api/users/${userId}`, { method: "DELETE" })
      .then(r => r.json())
      .then(d => {
        if (d.ok) {
          setUsers(users.filter(u => String(u.user_id) !== String(userId)));
        } else {
          alert(d.error || "移除失敗");
        }
      })
      .catch(() => alert("連線失敗"))
      .finally(() => setRemovingId(""));
  };

  const handleTogglePref = (userId, key, current) => {
    authFetch(`/api/users/${userId}/preferences`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ [key]: !current })
    })
      .then(r => r.json())
      .then(d => {
        if (d.error) {
          alert(d.error);
        } else {
          setUsers(users.map(u => 
            String(u.user_id) === String(userId)
              ? { ...u, [key]: d[key] }
              : u
          ));
        }
      })
      .catch(() => alert("連線失敗"));
  };

  const usersTotalPages = Math.max(1, Math.ceil(users.length / USERS_PAGE_SIZE));
  const pageUsers = users.slice((usersPage - 1) * USERS_PAGE_SIZE, usersPage * USERS_PAGE_SIZE);
  
  return (
    <div className="page-fill">
      <h1 className="page-title">用戶管理</h1>

      <div className="stats-grid">
        <StatCard label="用戶總數" value={users.length} accent="#0056b3" />
        <StatCard label="監控商品總數" value={totalProducts} accent="#16a34a" />
      </div>


      {loading && <div className="loading">載入中...</div>}

      {!loading && users.length > 0 && (
        <div className="card card-fill" style={{ marginTop: 20 }}>
          <div className="card-header">
            <span>共 {users.length} 位用戶</span>
          </div>
          <div className="table-scroll">
          <table className="table">
            <thead>
              <tr>
                <th rowSpan={2} style={{ width: 44 }} className="center">身份</th>
                <th rowSpan={2} style={{ width: 130 }}>用戶</th>
                <th colSpan={2} className="center" style={{ borderBottom: "1px solid #e5e7eb", fontSize: 11, color: "#6b7280", paddingBottom: 2 }}>清單功能</th>
                <th colSpan={3} className="center" style={{ borderBottom: "1px solid #e5e7eb", fontSize: 11, color: "#6b7280", paddingBottom: 2 }}>通知設定</th>
                <th rowSpan={2} style={{ width: 108 }} className="center">加入 / 活躍</th>
                <th rowSpan={2} style={{ width: 56 }} className="center">移除</th>
              </tr>
              <tr>
                <th style={{ width: 72 }} className="center">監控清單</th>
                <th style={{ width: 82 }} className="center">快訊上限</th>
                <th style={{ width: 56 }} className="center">邀請</th>
                <th style={{ width: 56 }} className="center">新上架</th>
                <th style={{ width: 56 }} className="center">降價</th>
              </tr>
            </thead>
            <tbody>
              {pageUsers.map(u => {
                const isAdminRow = String(u.user_id) === ADMIN_ID;
                const CELL_H = { display: "flex", alignItems: "center", justifyContent: "center", height: 28 };
                return (
                  <tr key={u.user_id}>
                    <td className="center" style={{ fontSize: 20 }}>
                      {isAdminRow ? "👑" : "👤"}
                    </td>
                    <td>
                      <div style={{ fontWeight: 600, fontSize: 13, lineHeight: 1.3 }}>
                        {u.username}
                        {isAdminRow && <span className="tag tag-orange" style={{ marginLeft: 6, fontSize: 10 }}>Admin</span>}
                      </div>
                      <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "monospace", marginTop: 1 }}>
                        {u.user_id}
                      </div>
                      {!isAdminRow && (
                        <NoteEditor
                          user={u}
                          onUpdate={(note) => setUsers(users.map(x =>
                            String(x.user_id) === String(u.user_id) ? { ...x, note } : x
                          ))}
                        />
                      )}
                    </td>
                    <td className="center">
                      <div style={CELL_H}>
                        <button
                          onClick={() => onSelectUser && onSelectUser(String(u.user_id))}
                          style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            gap: 4, width: 64, height: 26,
                            background: "#eff6ff", color: "#1d4ed8",
                            border: "1px solid #bfdbfe", borderRadius: 5, cursor: "pointer",
                            fontSize: 12, fontWeight: 600,
                          }}
                        >
                          📦 {u.product_count}
                        </button>
                      </div>
                    </td>
                    <td className="center">
                      <KeywordLimitEditor user={u} onUpdate={(newLimit) => setUsers(users.map(x => String(x.user_id) === String(u.user_id) ? { ...x, keyword_alert_limit: newLimit } : x))} />
                    </td>
                    <td className="center">
                      <div style={CELL_H}>
                        {isAdminRow ? (
                          <span style={{ fontSize: 11, color: "#16a34a", fontWeight: 600 }}>永久</span>
                        ) : (
                          <ToggleSwitch
                            on={!!u.can_invite}
                            loading={togglingId === String(u.user_id)}
                            onChange={() => toggleInvite(u.user_id, u.can_invite)}
                          />
                        )}
                      </div>
                    </td>
                    <td className="center">
                      <div style={CELL_H}>
                        <ToggleSwitch
                          on={u.notify_new_products !== false}
                          onChange={() => handleTogglePref(u.user_id, "notify_new_products", u.notify_new_products)}
                        />
                      </div>
                    </td>
                    <td className="center">
                      <div style={CELL_H}>
                        <ToggleSwitch
                          on={u.notify_price_drops !== false}
                          onChange={() => handleTogglePref(u.user_id, "notify_price_drops", u.notify_price_drops)}
                        />
                      </div>
                    </td>
                    <td className="center">
                      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                        <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "monospace", whiteSpace: "nowrap" }}>
                          {u.joined ? u.joined.slice(0, 10) : "—"}
                        </div>
                        <div style={{ fontSize: 11, color: "#6b7280", fontFamily: "monospace", whiteSpace: "nowrap" }}>
                          {u.last_active_at ? u.last_active_at.slice(5, 16) : "從未"}
                        </div>
                      </div>
                    </td>
                    <td className="center">
                      {!isAdminRow ? (
                        <button
                          onClick={() => handleRemove(u.user_id, u.username)}
                          disabled={removingId === String(u.user_id)}
                          style={{
                            display: "inline-flex", alignItems: "center", justifyContent: "center",
                            width: 46, height: 26,
                            background: "#fee2e2", color: "#991b1b",
                            border: "1px solid #fca5a5", borderRadius: 5, cursor: "pointer",
                            fontSize: 12, whiteSpace: "nowrap",
                          }}
                        >
                          {removingId === String(u.user_id) ? "…" : "🗑️"}
                        </button>
                      ) : (
                        <span style={{ fontSize: 12, color: "#d1d5db" }}>—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          <Pagination page={usersPage} totalPages={usersTotalPages} onPageChange={setUsersPage} />
        </div>
      )}
    </div>
  );
}


function InviteCodesPage() {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [label, setLabel] = useState("");
  const [creating, setCreating] = useState(false);
  const [newCode, setNewCode] = useState(null);
  const [deleting, setDeleting] = useState("");
  const [copied, setCopied] = useState("");
  const [filter, setFilter] = useState("unused");

  const load = () => {
    setLoading(true);
    authFetch("/api/invites").then(r => r.json()).then(data => {
      const list = Object.entries(data || {}).map(([code, info]) => ({
        code,
        label: info.label || "",
        created_at: info.created_at || info.created || "",
        used: !!info.used,
      }));
      list.sort((a, b) => b.created_at.localeCompare(a.created_at));
      setCodes(list);
    }).catch(() => setCodes([])).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleCreate = () => {
    if (!label.trim()) return;
    setCreating(true);
    setNewCode(null);
    authFetch("/api/invites", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ label: label.trim() }),
    }).then(r => r.json()).then(d => {
      if (d.code) {
        setNewCode(d.code); setLabel(""); setFilter("unused"); load();
        navigator.clipboard.writeText(`🔗 邀請朋友加入\n\n將以下連結傳送給朋友：\nhttps://t.me/CoTrackbot?start=${d.code}\n\n加入後可用同帳號登入網頁版：\nhttps://www.cotrackbot.com`);
        setCopied(d.code); setTimeout(() => setCopied(p => p === d.code ? "" : p), 3000);
      }
      else alert(d.error || "建立失敗");
    }).catch(() => alert("連線失敗")).finally(() => setCreating(false));
  };

  const handleDelete = (code) => {
    if (!window.confirm(`確定刪除邀請碼「${code}」？`)) return;
    setDeleting(code);
    authFetch(`/api/invites/${code}`, { method: "DELETE" })
      .then(r => r.json())
      .then(d => {
        if (d.ok) { setCodes(prev => prev.filter(x => x.code !== code)); if (newCode === code) setNewCode(null); }
        else alert(d.error || "刪除失敗");
      })
      .catch(() => alert("連線失敗"))
      .finally(() => setDeleting(""));
  };

  const copyMsg = (code) => {
    navigator.clipboard.writeText(
      `🔗 邀請朋友加入\n\n將以下連結傳送給朋友：\nhttps://t.me/CoTrackbot?start=${code}\n\n加入後可用同帳號登入網頁版：\nhttps://www.cotrackbot.com`
    );
    setCopied(code);
    setTimeout(() => setCopied(p => p === code ? "" : p), 2000);
  };

  const unusedCount = codes.filter(c => !c.used).length;
  const usedCount = codes.filter(c => c.used).length;
  const visible = filter === "unused" ? codes.filter(c => !c.used)
                : filter === "used"   ? codes.filter(c => c.used)
                : codes;

  const filterBtn = (key, label, count) => (
    <button onClick={() => setFilter(key)} style={{
      padding: "5px 14px", borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: "pointer",
      border: filter === key ? "none" : "1px solid #e5e7eb",
      background: filter === key ? "#2563eb" : "#fff",
      color: filter === key ? "#fff" : "#6b7280",
    }}>{label} {count}</button>
  );

  return (
    <div className="page-fill">
      <h1 className="page-title">邀請碼管理</h1>

      {/* 產生區 */}
      <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <input
          value={label}
          onChange={e => { setLabel(e.target.value); setNewCode(null); }}
          onKeyDown={e => e.key === "Enter" && !creating && label.trim() && handleCreate()}
          placeholder="這是給誰的？（例：LINE 小明）"
          style={{ padding: "7px 10px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 13, width: 220 }}
        />
        <button className="btn-primary" onClick={handleCreate} disabled={creating || !label.trim()}>
          {creating ? "產生中..." : "➕ 產生邀請碼"}
        </button>
      </div>

      {/* 篩選按鈕 + card */}
      {loading && <div className="loading">載入中...</div>}
      {!loading && codes.length === 0 && <div className="empty-state">尚無邀請碼，輸入備註後點「➕ 產生邀請碼」</div>}
      {!loading && codes.length > 0 && (
        <div className="card card-fill">
          <div className="card-header" style={{ display: "flex", alignItems: "center", gap: 8 }}>
            {filterBtn("unused", "未使用", unusedCount)}
            {filterBtn("used", "已使用", usedCount)}
            {filterBtn("all", "全部", codes.length)}
          </div>
          <div className="table-scroll">
            {visible.length === 0 ? (
              <div className="empty-state">無符合的邀請碼</div>
            ) : (
              <table className="table" style={{ tableLayout: "fixed", width: "100%" }}>
                <thead>
                  <tr>
                    <th style={{ width: 148 }}>邀請碼</th>
                    <th style={{ width: 180 }}>備註</th>
                    <th style={{ width: 136 }}>建立時間</th>
                    <th className="center" style={{ width: 136 }}>操作</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map(entry => (
                    <tr key={entry.code} style={entry.code === newCode ? { background: "#f0fdf4" } : {}}>
                      <td>
                        <code style={{ fontFamily: "monospace", fontWeight: 700, fontSize: 13, letterSpacing: 0.5, color: entry.used ? "#9ca3af" : "#1d4ed8" }}>
                          {entry.code}
                        </code>
                      </td>
                      <td style={{ fontSize: 13, color: entry.used ? "#9ca3af" : "#374151" }}>{entry.label || <span style={{ color: "#d1d5db" }}>—</span>}</td>
                      <td style={{ fontSize: 12, color: "#9ca3af", fontFamily: "monospace" }}>{entry.created_at}</td>
                      <td className="center">
                        {!entry.used ? (
                          <div style={{ display: "flex", gap: 6, justifyContent: "center" }}>
                            <button onClick={() => copyMsg(entry.code)}
                              style={{ padding: "3px 10px", background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe", borderRadius: 5, cursor: "pointer", fontSize: 12, whiteSpace: "nowrap" }}>
                              {copied === entry.code ? "✓ 已複製" : "📋 複製"}
                            </button>
                            <button onClick={() => handleDelete(entry.code)} disabled={deleting === entry.code}
                              style={{ padding: "3px 10px", background: "#fee2e2", color: "#991b1b", border: "1px solid #fca5a5", borderRadius: 5, cursor: "pointer", fontSize: 12 }}>
                              {deleting === entry.code ? "…" : "🗑️"}
                            </button>
                          </div>
                        ) : (
                          <span style={{ fontSize: 11, color: "#d1d5db" }}>—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function NotificationsPage() {
  const [items, setItems] = useState([]);
  const [notiPage, setNotiPage] = useState(1);
  const NOTI_PAGE_SIZE = 50;
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState("all");
  const [users, setUsers] = useState([]);

  // 讀取登入身份
  const authUser = (() => {
    try {
      return JSON.parse(localStorage.getItem("auth_user") || "null");
    } catch (e) { return null; }
  })();
  const isAdmin = authUser?.role === "admin";

  // admin 可以選擇用戶；非 admin 固定自己
  const [selectedUser, setSelectedUser] = useState(
    isAdmin ? "all" : (authUser?.user_id || "")
  );

  // 載入用戶列表（給 admin 選）
  useEffect(() => {
    if (!isAdmin) return;
    authFetch("/api/users")
      .then(r => r.json())
      .then(d => setUsers(Array.isArray(d) ? d : []))
      .catch(() => setUsers([]));
  }, [isAdmin]);

  // 載入通知
  useEffect(() => {
    setLoading(true);
    const url = selectedUser === "all" 
      ? "/api/notifications?limit=500"
      : `/api/notifications?limit=500&user_id=${selectedUser}`;
    authFetch(url)
      .then(r => r.json())
      .then(d => setItems(Array.isArray(d) ? d : []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [selectedUser]);

  const typeStyle = (type) => {
    const map = {
      "降價":   { bg: "#dcfce7", color: "#166534", icon: "🔥" },
      "漲價":   { bg: "#fee2e2", color: "#991b1b", icon: "📈" },
      "補貨":   { bg: "#dcfce7", color: "#166534", icon: "🎉" },
      "缺貨":   { bg: "#fee2e2", color: "#991b1b", icon: "⚠️" },
      "新上架": { bg: "#dbeafe", color: "#1e40af", icon: "✨" },
      "即將上架": { bg: "#e0e7ff", color: "#3730a3", icon: "🔜" },
      "特價":   { bg: "#fef3c7", color: "#92400e", icon: "🏷️" },
      "特價結束": { bg: "#f3f4f6", color: "#6b7280", icon: "🏷️" },
      "下架":   { bg: "#f3f4f6", color: "#6b7280", icon: "🗑️" },
      "出清":   { bg: "#ffedd5", color: "#9a3412", icon: "🏷️" },
    };
    return map[type] || { bg: "#f3f4f6", color: "#374151", icon: "📌" };
  };

  const typeCounts = items.reduce((acc, n) => {
    acc[n.type] = (acc[n.type] || 0) + 1;
    return acc;
  }, {});

  const filteredItems = filterType === "all" 
    ? items 
    : items.filter(n => n.type === filterType);

  const sortedItems = [...filteredItems].sort((a, b) => (b.time || "").localeCompare(a.time || ""));

  const formatPriceChange = (n) => {
    if (n.old != null && n.new != null) {
      const oldNum = parseFloat(n.old);
      const newNum = parseFloat(n.new);
      if (!isNaN(oldNum) && !isNaN(newNum)) {
        const diff = newNum - oldNum;
        const pct = ((diff / oldNum) * 100).toFixed(1);
        const isDown = diff < 0;
        return (
          <span>
            NT${oldNum.toLocaleString()} → <strong style={{ color: isDown ? "#16a34a" : "#dc2626" }}>NT${newNum.toLocaleString()}</strong>
            <span style={{ marginLeft: 6, color: isDown ? "#16a34a" : "#dc2626", fontSize: 12 }}>
              ({isDown ? "↓" : "↑"}{Math.abs(pct)}%)
            </span>
          </span>
        );
      }
    }
    if (n.type === "補貨") return <span style={{ color: "#16a34a", fontSize: 12 }}>缺貨 → 有貨</span>;
    if (n.type === "缺貨") return <span style={{ color: "#dc2626", fontSize: 12 }}>有貨 → 缺貨</span>;
    if (n.type === "新上架") return <span style={{ color: "#1e40af", fontSize: 12 }}>全新商品上架</span>;
    if (n.type === "即將上架") return <span style={{ color: "#3730a3", fontSize: 12 }}>已新增，尚未開售</span>;
    if (n.type === "特價") return <span style={{ color: "#92400e", fontSize: 12 }}>特價開始</span>;
    if (n.type === "特價結束") return <span style={{ color: "#6b7280", fontSize: 12 }}>特價結束</span>;
    return <span className="muted">—</span>;
  };

  const notiTotalPages = Math.max(1, Math.ceil(sortedItems.length / NOTI_PAGE_SIZE));
  const sortedPageItems = sortedItems.slice((notiPage - 1) * NOTI_PAGE_SIZE, notiPage * NOTI_PAGE_SIZE);
  
  return (
    <div className="page-fill">
      <h1 className="page-title">通知紀錄</h1>

      {/* 用戶選擇（僅 admin） */}
      {isAdmin && (
        <div style={{ marginBottom: 16, display: "flex", alignItems: "center", gap: 12 }}>
          <label style={{ fontSize: 14, fontWeight: 500 }}>查看用戶：</label>
          <select
            value={selectedUser}
            onChange={e => setSelectedUser(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, minWidth: 200 }}
          >
            <option value="all">全部用戶（包含舊紀錄）</option>
            {users.map(u => (
              <option key={u.user_id} value={u.user_id}>
                {u.username}
              </option>
            ))}
          </select>
        </div>
      )}

      {/* 統計列 + 篩選 */}
      <div className="card" style={{ padding: "10px 16px", marginBottom: 12, display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: "#374151", whiteSpace: "nowrap" }}>
          共 <span style={{ color: "#0056b3" }}>{items.length.toLocaleString()}</span> 筆
        </span>
        <div style={{ width: 1, height: 16, background: "#e5e7eb", flexShrink: 0 }} />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", flex: 1 }}>
          <button
            onClick={() => setFilterType("all")}
            className={filterType === "all" ? "btn-primary" : "btn-secondary"}
            style={{ padding: "3px 10px", fontSize: 12 }}
          >全部</button>
          {Object.entries(typeCounts).map(([type, count]) => {
            const st = typeStyle(type);
            const active = filterType === type;
            return (
              <button
                key={type}
                onClick={() => setFilterType(type)}
                style={{
                  padding: "3px 10px", fontSize: 12, borderRadius: 20, border: "none", cursor: "pointer",
                  background: active ? st.color : st.bg,
                  color: active ? "#fff" : st.color,
                  fontWeight: active ? 600 : 400,
                }}
              >
                {st.icon} {type} {count}
              </button>
            );
          })}
        </div>
      </div>

      {loading && <div className="loading">載入中...</div>}

      {!loading && sortedItems.length === 0 && (
        <div className="empty-state">沒有通知紀錄</div>
      )}

      {!loading && sortedItems.length > 0 && (
        <div className="card card-fill">
          <div className="card-header">
            <span>共 {sortedItems.length.toLocaleString()} 筆紀錄</span>
          </div>
          <div className="table-scroll">
        <table className="table">
            <thead>
              <tr>
                <th style={{ width: 110 }} className="center">類型</th>
                <th>商品名稱</th>
                <th style={{ width: 220 }} className="left">變化</th>
                <th style={{ width: 140 }} className="center">時間</th>
              </tr>
            </thead>
            <tbody>
              {sortedPageItems.map((n, i) => {
                const st = typeStyle(n.type);
                return (
                  <tr key={i}>
                    <td className="center">
                      <span style={{
                        padding: "4px 10px", background: st.bg, color: st.color,
                        borderRadius: 4, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap"
                      }}>
                        {st.icon} {n.type}
                      </span>
                    </td>
                    <td>
                      <a href={n.url} target="_blank" rel="noreferrer" className="link">
                        {n.name}
                      </a>
                    </td>
                    <td className="center mono small">
                      {formatPriceChange(n)}
                    </td>
                    <td className="center mono small muted">{n.time}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
          <Pagination page={notiPage} totalPages={notiTotalPages} onPageChange={setNotiPage} />
        </div>
      )}
    </div>
  );
}

function ChartsPage() {
  const [productId, setProductId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [days, setDays] = useState(30);
  const [customYear, setCustomYear] = useState("");
  const [customMonthNum, setCustomMonthNum] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [cardData, setCardData] = useState(null);
  const [generatingCard, setGeneratingCard] = useState(false);
  const [productStatus, setProductStatus] = useState(null);
  const [dataFetchedAt, setDataFetchedAt] = useState(null);
  const cardRef = useRef(null);

  // 圖卡固定抓最近 30 天，不受頁面上目前選的時間範圍影響
  const generateCard = async () => {
    if (!productId) return;
    setGeneratingCard(true);
    try {
      const [r, statusR] = await Promise.all([
        authFetch(`/api/products/${encodeURIComponent(productId)}/history?days=30`),
        authFetch(`/api/catalog/product/${encodeURIComponent(productId)}`)
      ]);
      const d = await r.json();
      const statusD = await statusR.json().catch(() => ({}));
      const hist = d.history || [];
      const cd = groupHistoryByDay(hist).map((h, idx, arr) => {
        const changed = idx === 0 || arr[idx - 1].price !== h.price;
        return {
          label: changed ? h.time.substring(5, 16) : h.time.substring(5, 10),
          price: h.price,
          changed
        };
      });
      const cdPrices = cd.map(x => x.price).filter(p => p != null);
      const lastPrice = cdPrices.length > 0 ? cdPrices[cdPrices.length - 1] : null;
      const maxP = cdPrices.length > 0 ? Math.max(...cdPrices) : null;
      const minP = cdPrices.length > 0 ? Math.min(...cdPrices) : null;
      const lastHist = hist.length > 0 ? hist[hist.length - 1] : null;
      setCardData({
        name: d.name, productId, price: lastPrice, maxPrice: maxP, minPrice: minP, chartData: cd,
        time: lastHist ? fmtNow() : null,
        stockLabel: lastHist ? priceStockLabel(statusD.status, !!lastHist.in_stock) : null
      });
    } catch (e) {
      alert("圖卡生成失敗，請稍後再試");
      setGeneratingCard(false);
    }
  };

  // cardData 就緒後，等隱藏的卡片 DOM 畫出來，再截圖並觸發下載
  // 折線消失的真正原因是 ResponsiveContainer 用 ResizeObserver 非同步量測寬高，
  // 離屏（left:-9999px）+ 剛掛載時常常還沒量完就被截圖；改用固定 width/height
  // 的 LineChart（不透過 ResponsiveContainer）第一次渲染就直接有完整尺寸，
  // 不需要等量測。foreignObjectRendering 模式試過但在 Safari/iOS 會整張變黑，已還原。
  useEffect(() => {
    if (!cardData || !cardRef.current) return;
    const timer = setTimeout(() => {
      html2canvas(cardRef.current, { backgroundColor: "#ffffff", scale: 2 }).then(canvas => {
        const safeName = (cardData.name || "商品").replace(/[\\/:*?"<>|]/g, "");
        const dateStr = new Date().toISOString().slice(0, 10);
        const fileName = `${safeName}_價格走勢_${dateStr}.png`;
        canvas.toBlob(async (blob) => {
          const file = new File([blob], fileName, { type: "image/png" });
          // 手機支援 Web Share API 就直接跳分享面板；桌面一律直接下載
          if (IS_MOBILE_DEVICE && navigator.canShare && navigator.canShare({ files: [file] })) {
            try {
              const shareTitle = cardData.name || "價格走勢";
              const productUrl = cardData.productId ? `https://www.costco.com.tw/p/${cardData.productId}` : "";
              await navigator.share({
                files: [file],
                title: shareTitle,
                text: productUrl ? `${shareTitle}\n${productUrl}` : shareTitle,
              });
            } catch (e) {
              // 使用者按取消也會丟這個例外，不算失敗，不用跳警示
            }
          } else {
            const link = document.createElement("a");
            link.download = fileName;
            link.href = URL.createObjectURL(blob);
            link.click();
            URL.revokeObjectURL(link.href);
          }
          setCardData(null);
          setGeneratingCard(false);
        }, "image/png");
      }).catch(() => {
        alert("圖卡生成失敗，請稍後再試");
        setCardData(null);
        setGeneratingCard(false);
      });
    }, 200);
    return () => clearTimeout(timer);
  }, [cardData]);

  // 從 localStorage 讀取上次查的 product_id
  useEffect(() => {
    const last = localStorage.getItem("charts_last_pid");
    if (last) {
      setProductId(last);
    }
  }, []);

  // 商品目前是否下架（status），跟價格/庫存歷史是分開的資料源，只需隨 productId 重抓
  useEffect(() => {
    if (!productId) { setProductStatus(null); return; }
    authFetch(`/api/catalog/product/${encodeURIComponent(productId)}`)
      .then(r => r.json())
      .then(d => setProductStatus(d.status || null))
      .catch(() => setProductStatus(null));
  }, [productId]);

  // 載入歷史資料
  const customMonth = (customYear && customMonthNum) ? `${customYear}-${customMonthNum}` : "";

  useEffect(() => {
    if (!productId) return;
    setLoading(true);
    setError("");
    const qs = customMonth ? `month=${customMonth}` : `days=${days}`;
    authFetch(`/api/products/${encodeURIComponent(productId)}/history?${qs}`)
      .then(r => r.json())
      .then(d => {
        if (d.history && d.history.length > 0) {
          setData(d);
          setDataFetchedAt(fmtNow());
          localStorage.setItem("charts_last_pid", productId);
        } else if (d.price_requires_browser) {
          setData(null);
          setError("此商品 Costco 官網有標價，但須登入會員才看得到，系統暫時無法自動取得價格，請至商品頁登入查看現價");
        } else {
          setData(null);
          setError("找不到此商品的歷史紀錄");
        }
      })
      .catch(() => setError("連線失敗"))
      .finally(() => setLoading(false));
  }, [productId, days, customMonth]);

  // 搜尋商品
  useEffect(() => {
    if (!searchQuery.trim()) { setSearchResults([]); return; }
    const q = searchQuery.trim();
    const urlMatch = q.match(/\/p\/([^\/?\s]+)/);
    let sq = urlMatch ? urlMatch[1] : q;
    
    const timer = setTimeout(() => {
      setSearching(true);
      const idPattern = /^[0-9]+(-[A-Z0-9]+)*$/i;
      const url = idPattern.test(sq)
        ? `/api/catalog/product/${encodeURIComponent(sq)}`
        : `/api/catalog/search?q=${encodeURIComponent(sq)}&limit=20&no_tire=1&no_warehouse=1`;
      authFetch(url)
        .then(r => r.json())
        .then(d => {
          if (Array.isArray(d)) setSearchResults(d);
          else if (d.product_id) setSearchResults([d]);
          else if (d.items && Array.isArray(d.items)) setSearchResults(d.items);
          else setSearchResults([]);
        })
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // 處理圖表資料
  const chartData = (data?.history ? groupHistoryByDay(data.history) : []).map((h, idx, arr) => {
    const changed = idx === 0 || arr[idx - 1].price !== h.price;
    return {
      time: h.time.substring(5, 16), // MM-DD HH:MM，完整時間，給 tooltip 用
      label: changed ? h.time.substring(5, 16) : h.time.substring(5, 10),
      price: h.price,
      in_stock: h.in_stock ? 1 : 0,
      changed
    };
  });

  const prices = chartData.map(d => d.price).filter(p => p);
  const minPrice = prices.length > 0 ? Math.min(...prices) : 0;
  const maxPrice = prices.length > 0 ? Math.max(...prices) : 0;
  const avgPrice = prices.length > 0 ? prices.reduce((a, b) => a + b, 0) / prices.length : 0;
  const currentPrice = prices.length > 0 ? prices[prices.length - 1] : 0;
  const firstPrice = prices.length > 0 ? prices[0] : 0;
  const totalChange = firstPrice > 0 ? ((currentPrice - firstPrice) / firstPrice * 100).toFixed(1) : 0;
  const lastHistory = data?.history?.length > 0 ? data.history[data.history.length - 1] : null;
  const currentTime = dataFetchedAt;
  const currentStockLabel = lastHistory ? priceStockLabel(productStatus, !!lastHistory.in_stock) : null;

  return (
    <div className="page">
      <h1 className="page-title">價格走勢</h1>

      {/* 搜尋區 */}
      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: 16, marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, color: "#374151" }}>
          🔍 選擇商品
        </div>
        <input
          type="text"
          placeholder="輸入商品 ID 或關鍵字..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          style={{ width: "100%", padding: "10px 14px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 14, outline: "none", boxSizing: "border-box" }}
        />

        {searching && <div style={{ marginTop: 10, color: "#6b7280", fontSize: 13 }}>搜尋中...</div>}

        {searchResults.length > 0 && (
          <div style={{ marginTop: 12, maxHeight: 240, overflowY: "auto", background: "white", border: "1px solid #e5e7eb", borderRadius: 6 }}>
            {searchResults.slice(0, 20).map(p => (
              <div key={p.product_id}
                onClick={() => { setProductId(p.product_id); setSearchQuery(""); setSearchResults([]); }}
                style={{
                  padding: "10px 14px", borderBottom: "1px solid #f3f4f6", cursor: "pointer"
                }}
                onMouseEnter={e => e.currentTarget.style.background = "#f3f4f6"}
                onMouseLeave={e => e.currentTarget.style.background = "white"}
              >
                <div style={{ fontSize: 14, color: "#111827", marginBottom: 2 }}>{p.name}</div>
                <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "monospace" }}>
                  ID: {p.product_id} {p.price ? `· NT$${p.price.toLocaleString()}` : ""}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 時間範圍 */}
      {productId && (
        <div style={{ marginBottom: 16, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontSize: 14, fontWeight: 500 }}>時間範圍：</span>
          {[7, 14, 30, 60, 90].map(d => (
            <button
              key={d}
              onClick={() => { setDays(d); setCustomYear(""); setCustomMonthNum(""); }}
              className={!customMonth && d === days ? "btn-primary" : "btn-secondary"}
            >
              {d} 天
            </button>
          ))}
          <span style={{ color: "#d1d5db" }}>|</span>
          <span style={{ fontSize: 13, color: "#6b7280" }}>📅 查詢月份：</span>
          <select
            value={customYear}
            onChange={e => setCustomYear(e.target.value)}
            style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13 }}
          >
            <option value="">年份</option>
            {Array.from({ length: 3 }, (_, i) => new Date().getFullYear() - i).map(y => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
          <select
            value={customMonthNum}
            onChange={e => setCustomMonthNum(e.target.value)}
            disabled={!customYear}
            style={{ padding: "6px 10px", borderRadius: 6, border: "1px solid #d1d5db", fontSize: 13 }}
          >
            <option value="">月份</option>
            {Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0")).map(m => (
              <option key={m} value={m}>{Number(m)} 月</option>
            ))}
          </select>
        </div>
      )}

      {loading && <div className="loading">載入中...</div>}

      {!loading && error && (
        <div className="empty-state">{error}</div>
      )}

      {!loading && data && chartData.length > 0 && (
        <>
          {/* 商品資訊 */}
          <div style={{ marginBottom: 16, padding: "16px 20px", background: "white", border: "1px solid #e5e7eb", borderRadius: 8 }}>
            <div style={{ fontSize: 22, fontWeight: 700, color: "#111827" }}>{data.name}</div>
            <div style={{ fontSize: 13, color: "#9ca3af", marginTop: 4, fontFamily: "monospace" }}>商品編號:#{data.product_id}</div>
          </div>

          {/* 統計卡片 */}
          <div style={{ display: "flex", justifyContent: "space-around", padding: "16px 20px", background: "white", border: "1px solid #e5e7eb", borderRadius: 8 }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 4 }}>最低價</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "#16a34a" }}>NT${minPrice.toLocaleString()}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 4 }}>現價{currentStockLabel ? `（${currentStockLabel}）` : ""}</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "#0056b3" }}>NT${currentPrice.toLocaleString()}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 4 }}>最高價</div>
              <div style={{ fontSize: 24, fontWeight: 700, color: "#dc2626" }}>NT${maxPrice.toLocaleString()}</div>
            </div>
          </div>

          {!customMonth && firstPrice !== currentPrice && (
            <div style={{
              marginTop: 12, padding: "10px 14px",
              background: totalChange < 0 ? "#dcfce7" : "#fee2e2",
              color: totalChange < 0 ? "#166534" : "#991b1b",
              borderRadius: 8, fontSize: 14
            }}>
              {days} 天總變化：{totalChange < 0 ? "▼" : "▲"} {Math.abs(totalChange)}%
              （NT${firstPrice.toLocaleString()} → NT${currentPrice.toLocaleString()}）
            </div>
          )}

          {/* 圖表 */}
          <div style={{ marginTop: 24, padding: 16, background: "white", border: "1px solid #e5e7eb", borderRadius: 8 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 600, color: "#374151" }}>
                📈 價格走勢圖
              </div>
              <button onClick={generateCard} disabled={generatingCard}
                style={{ padding: "6px 14px", fontSize: 13, borderRadius: 6, background: "#0056b3", color: "#fff",
                  border: "none", cursor: generatingCard ? "default" : "pointer", opacity: generatingCard ? 0.6 : 1 }}>
                {generatingCard ? "生成中…" : "📸 生成圖卡"}
              </button>
            </div>
            <div style={{ position: "relative" }}>
              <img src="/logo192.png" alt="" style={{
                position: "absolute", top: "40%", left: "50%", transform: "translate(-50%, -50%)",
                width: 160, height: 160, opacity: 0.08, zIndex: 0, pointerEvents: "none"
              }} />
              <div style={{ position: "relative", zIndex: 1 }}>
                <ResponsiveContainer width="100%" height={400}>
                  <LineChart data={chartData} margin={{ top: 10, right: 30, left: 10, bottom: 30 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="label" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={60} interval={Math.max(0, Math.ceil(chartData.length / 12) - 1)} />
                    <YAxis 
                      domain={['dataMin - 100', 'dataMax + 100']}
                      tick={{ fontSize: 11 }}
                      tickFormatter={(v) => `NT$${v.toLocaleString()}`}
                    />
                    <Tooltip 
                      formatter={(value) => [`NT$${value.toLocaleString()}`, "價格"]}
                      labelFormatter={(_, payload) => (payload && payload[0]) ? payload[0].payload.time : ""}
                      contentStyle={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 6 }}
                    />
                    <Line 
                      type="monotone" 
                      dataKey="price" 
                      stroke="#0056b3" 
                      strokeWidth={2}
                      dot={changePointDot}
                      activeDot={{ r: 6 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        </>
      )}

      {!loading && !productId && !error && (
        <div className="empty-state">請輸入商品 ID 或搜尋商品名稱開始查看走勢</div>
      )}

      {/* 隱藏的分享圖卡：畫在畫面外，只給 html2canvas 截圖用 */}
      {cardData && (
        <div ref={cardRef} style={{
          position: "fixed", left: "-9999px", top: 0,
          width: 800, background: "#fff", padding: 40, boxSizing: "border-box",
          display: "flex", flexDirection: "column",
          fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Microsoft JhengHei', sans-serif"
        }}>
          <div style={{ fontSize: 26, fontWeight: 700, color: "#111827", marginBottom: 6 }}>{cardData.name}</div>
          <div style={{ fontSize: 14, color: "#9ca3af", fontFamily: "monospace", marginBottom: 16 }}>商品編號:#{cardData.productId}</div>

          <div style={{ position: "relative", width: 720, height: 280 }}>
            <img src="/logo192.png" alt="" style={{
              position: "absolute", top: "40%", left: "50%", transform: "translate(-50%, -50%)",
              width: 160, height: 160, opacity: 0.08, zIndex: 0, pointerEvents: "none"
            }} />
            <div style={{ position: "relative", zIndex: 1 }}>
              <LineChart width={720} height={280} data={cardData.chartData} margin={{ top: 10, right: 20, left: 15, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="label" tick={{ fontSize: 12 }} angle={-20} textAnchor="end" height={40} interval={Math.max(0, Math.ceil(cardData.chartData.length / 12) - 1)} />
                <YAxis width={75} domain={['dataMin - 100', 'dataMax + 100']} tick={{ fontSize: 12 }} tickFormatter={(v) => `NT$${v.toLocaleString()}`} />
                <Line type="monotone" dataKey="price" stroke="#0056b3" strokeWidth={3} dot={changePointDot} isAnimationActive={false} />
              </LineChart>
            </div>
          </div>

          <div style={{ display: "flex", justifyContent: "space-around", marginTop: 16, paddingTop: 16, borderTop: "1px solid #f3f4f6" }}>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 4 }}>最低價</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#16a34a" }}>{cardData.minPrice != null ? `NT$${cardData.minPrice.toLocaleString()}` : "—"}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 4 }}>現價{cardData.stockLabel ? `（${cardData.stockLabel}）` : ""}</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#0056b3" }}>{cardData.price != null ? `NT$${cardData.price.toLocaleString()}` : "—"}</div>
            </div>
            <div style={{ textAlign: "center" }}>
              <div style={{ fontSize: 12, color: "#9ca3af", marginBottom: 4 }}>最高價</div>
              <div style={{ fontSize: 22, fontWeight: 700, color: "#dc2626" }}>{cardData.maxPrice != null ? `NT$${cardData.maxPrice.toLocaleString()}` : "—"}</div>
            </div>
          </div>

          <div style={{ textAlign: "center", marginTop: 16, fontSize: 12, color: "#9ca3af" }}>
            <div>Cotrackbot 價格及庫存監控 · https://www.cotrackbot.com</div>
            {cardData.time && <div style={{ marginTop: 4 }}>製表時間：{cardData.time}</div>}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Dashboard ───────────────────────────────────────────────────────────────
const _dc = {
  background: "#fff",
  border: "1px solid #e5e7eb",
  borderRadius: 12,
  marginBottom: 16,
  overflow: "hidden",
};
const _dh = {
  padding: "12px 20px",
  borderBottom: "1px solid #f3f4f6",
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
};
const _dt = { fontWeight: 600, fontSize: 13, color: "#374151" };
const _dp = { padding: 16 };
const _dg4 = { display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 10 };

// 語意色：綠=好事 紅=壞事 橙=注意 藍=資訊 灰=中性
const C = { green: "#16a34a", red: "#dc2626", amber: "#d97706", blue: "#2563eb", gray: "#9ca3af" };

function CompactTile({ label, value, sub, color = "#9ca3af", onClick }) {
  const click = !!onClick;
  return (
    <div onClick={onClick} role={click ? "button" : undefined}
      style={{ background: "#fafafa", border: "1px solid #e5e7eb", borderRadius: 6, padding: "8px 10px",
        cursor: click ? "pointer" : "default", transition: "border-color 0.15s" }}
      onMouseEnter={click ? e => { e.currentTarget.style.borderColor = color; } : undefined}
      onMouseLeave={click ? e => { e.currentTarget.style.borderColor = "#e5e7eb"; } : undefined}
    >
      <div style={{ fontSize: 17, fontWeight: 700, color, lineHeight: 1.1 }}>{value?.toLocaleString() ?? "—"}</div>
      <div style={{ fontSize: 11, color: "#6b7280", marginTop: 2 }}>{label}</div>
      {sub != null && <div style={{ fontSize: 10, color: "#9ca3af", marginTop: 1 }}>累計 {sub?.toLocaleString() ?? "—"}</div>}
    </div>
  );
}

function fmtListedTime(s) {
  if (!s) return { date: "—", time: "" };
  const d = new Date(s.replace(" ", "T"));
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return { date: `${yyyy}/${mm}/${dd}`, time: d.toTimeString().slice(0, 5) };
}

function Dashboard({ onGoMyList, onGoUsers, onGoCatalogSale, onGoCatalogCoupon, onGoCatalog, onGoCatalogView, onGoEvents, onGoSettings }) {
  const [stats, setStats] = useState(null);
  const [catalog, setCatalog] = useState(null);
  const [newProducts, setNewProducts] = useState([]);
  const [newPage, setNewPage] = useState(1);
  const [newTotalPages, setNewTotalPages] = useState(1);
  const [newTotal, setNewTotal] = useState(0);
  const [newLoading, setNewLoading] = useState(true);
  const isAdmin = JSON.parse(localStorage.getItem("auth_user") || "{}").role === "admin";
  const NEW_PAGE_SIZE = 20;

  useEffect(() => {
    authFetch("/api/stats").then(r => r.json()).then(setStats).catch(() => {});
    authFetch("/api/catalog/stats").then(r => r.json()).then(setCatalog).catch(() => {});
  }, []);

  useEffect(() => {
    setNewLoading(true);
    authFetch(`/api/catalog/new?page=${newPage}&page_size=${NEW_PAGE_SIZE}&no_tire=1&no_warehouse=1`)
      .then(r => r.json())
      .then(d => {
        setNewProducts(d.items || []);
        setNewTotalPages(d.total_pages || 1);
        setNewTotal(d.total || 0);
      })
      .catch(() => setNewProducts([]))
      .finally(() => setNewLoading(false));
  }, [newPage]);

  return (
    <div className="page-fill">
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", marginBottom: 12, flexShrink: 0 }}>
        <h1 style={{ fontSize: 20, fontWeight: 700, color: "#111827", margin: 0 }}>總覽</h1>
        {catalog?.last_stock_update && (
          <span style={{ fontSize: 11, color: "#9ca3af" }}>最後更新：{catalog.last_stock_update}</span>
        )}
      </div>

      {/* tiles card — 固定高度，不填滿 */}
      <div className="card" style={{ padding: "14px 20px", display: "flex", flexDirection: "column", gap: 0, flexShrink: 0, marginBottom: 12 }}>

        {/* 全站商品庫 */}
        <div style={{ paddingBottom: 16, marginBottom: 16, borderBottom: "1px solid #f3f4f6" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", letterSpacing: "0.05em", textTransform: "uppercase" }}>全站商品庫</div>
            <span style={{ fontSize: 10, color: "#d1d5db" }}>排除賣場/酒/胎</span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "6px 10px", marginBottom: 8 }}>
            <CompactTile label="SKU 總數"   value={catalog?.total}          color={C.blue} />
            <CompactTile label="獨立商品款" value={catalog?.unique_products} color={C.blue} />
            <CompactTile label="線上在售"   value={catalog?.with_price}      color={C.blue} />
            <CompactTile label="賣場限定"   value={catalog?.warehouse_only}  color={C.gray} />
          </div>
          <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 8, display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "6px 8px" }}>
            <CompactTile label="特價" value={catalog?.on_coupon}    color={C.amber} onClick={() => onGoCatalogView("discount")} />
            <CompactTile label="出清" value={catalog?.on_sale}      color={C.amber} onClick={() => onGoCatalogView("clearance")} />
            <CompactTile label="有貨" value={catalog?.in_stock}     color={C.green} onClick={() => onGoCatalogView("hot")} />
            <CompactTile label="缺貨" value={catalog?.out_of_stock} color={C.red}   onClick={() => onGoCatalogView("out_of_stock")} />
          </div>
        </div>

        {/* 今日動態 */}
        <div style={{ paddingBottom: 16, marginBottom: 16, borderBottom: "1px solid #f3f4f6" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", letterSpacing: "0.05em", textTransform: "uppercase" }}>今日動態</div>
            <button onClick={() => onGoEvents("")} style={{ fontSize: 11, color: C.blue, background: "none", border: "none", cursor: "pointer", fontWeight: 500, padding: 0 }}>全部 ›</button>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "6px 8px", marginBottom: 8 }}>
            <CompactTile label="降價" value={catalog?.today_discounts} sub={catalog?.total_discounts} color={C.green} onClick={() => onGoEvents("price_down")} />
            <CompactTile label="漲價" value={catalog?.today_raises}    sub={catalog?.total_raises}    color={C.red}   onClick={() => onGoEvents("price_up")} />
            <CompactTile label="補貨" value={catalog?.today_restocks}  sub={catalog?.total_restocks}  color={C.green} onClick={() => onGoEvents("stock_in")} />
            <CompactTile label="完售" value={catalog?.today_sold_out}  sub={catalog?.total_sold_out}  color={C.red}   onClick={() => onGoEvents("stock_out")} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "6px 8px" }}>
            <CompactTile label="少量" value={catalog?.today_low_stock} sub={catalog?.total_low_stock} color={C.amber} onClick={() => onGoEvents("stock_low")} />
            <CompactTile label="出清" value={catalog?.today_clearance} sub={catalog?.total_clearance} color={C.amber} onClick={() => onGoEvents("clearance")} />
            <CompactTile label="新上架" value={catalog?.today_listed}    sub={catalog?.total_listed}    color={C.blue}  onClick={() => onGoEvents("listed")} />
            <CompactTile label="下架" value={catalog?.today_delisted}  sub={catalog?.total_delisted}  color={C.gray}  onClick={() => onGoEvents("delisted")} />
          </div>
        </div>

        {/* 我的追蹤 */}
        <div>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", letterSpacing: "0.05em", marginBottom: 10, textTransform: "uppercase" }}>我的追蹤</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: "6px 10px", marginBottom: 10 }}>
            <CompactTile label="監控商品" value={stats?.total_products} color={C.blue}  onClick={() => onGoMyList("all")} />
            <CompactTile label="有庫存"   value={stats?.in_stock}       color={C.green} onClick={() => onGoMyList("in_stock")} />
            <CompactTile label="缺貨中"   value={stats?.out_of_stock}   color={C.red}   onClick={() => onGoMyList("out_of_stock")} />
            {isAdmin
              ? <CompactTile label="綁定用戶" value={stats?.total_users} color={C.blue} onClick={onGoUsers} />
              : <CompactTile label="特價中"   value={stats?.on_sale}     color={C.amber} onClick={onGoCatalogCoupon} />
            }
          </div>
          <div style={{ borderTop: "1px solid #f3f4f6", paddingTop: 10, display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 32, height: 32, background: "#0088cc", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, flexShrink: 0 }}>✈️</div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 600, fontSize: 12, marginBottom: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                <a href="https://t.me/CoTrackbot" target="_blank" rel="noreferrer" style={{ color: "#0088cc", textDecoration: "none" }}>@CoTrackbot</a>
              </div>
              <div style={{ fontSize: 11, color: "#9ca3af" }}>
                {isAdmin ? <>綁定 <strong style={{ color: "#374151" }}>{stats?.total_users?.toLocaleString() ?? "—"}</strong> 用戶</> : "通知偏好至我的設定"}
              </div>
            </div>
            {isAdmin
              ? <button onClick={onGoUsers} style={{ padding: "5px 10px", background: C.blue, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600, flexShrink: 0 }}>管理</button>
              : <button onClick={onGoSettings} style={{ padding: "5px 10px", background: C.blue, color: "#fff", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 600, flexShrink: 0 }}>設定</button>
            }
          </div>
        </div>

      </div>

      {/* 最新上架商品 — 填滿剩餘高度 */}
      <div className="card card-fill">
        <div className="card-header" style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <span>最新上架商品</span>
          <span style={{ display: "flex", alignItems: "center", gap: 12 }}>
            {newTotal > 0 && <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>共 {newTotal.toLocaleString()} 件</span>}
            <button onClick={() => onGoCatalogView("new")} style={{ fontSize: 11, color: C.blue, background: "none", border: "none", cursor: "pointer", fontWeight: 500, padding: 0 }}>查看清單 ›</button>
          </span>
        </div>
        <div className="table-scroll">
          {newLoading && <div className="loading">載入中...</div>}
          {!newLoading && newProducts.length === 0 && <div className="empty-state">暫無上架商品</div>}
          {!newLoading && newProducts.length > 0 && (
            <table className="table">
              <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                <tr>
                  <th>商品名稱</th>
                  <th style={{ width: 120 }} className="center">商品ID</th>
                  <th style={{ width: 80 }} className="center">庫存</th>
                  <th style={{ width: 110 }} className="right">售價</th>
                  <th style={{ width: 110 }} className="center">上架時間</th>
                </tr>
              </thead>
              <tbody>
                {newProducts.map(p => (
                  <tr key={p.product_id}>
                    <td>
                      <a href={p.url} target="_blank" rel="noreferrer" className="link product-name" title={p.name}>{p.name}</a>
                    </td>
                    <td className="mono small muted center">{p.base_id}</td>
                    <td className="center">
                      <StockBadge status={p.stock_status} added_at={p.added_at} product_status={p.status} showNew={false} />
                    </td>
                    <td className="mono right">{p.price ? `NT$${Number(p.price).toLocaleString()}` : "—"}</td>
                    <td className="mono small muted center" style={{ lineHeight: 1.4 }}>
                      <div>{fmtListedTime(p.added_at).date}</div>
                      <div>{fmtListedTime(p.added_at).time}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <Pagination page={newPage} totalPages={newTotalPages} onPageChange={setNewPage} />
      </div>

    </div>
  );
}
function StatCard({ label, value, accent, onClick, sub }) {
  return (
    <div
      className={`stat-card ${onClick ? "clickable" : ""}`}
      onClick={onClick}
      role={onClick ? "button" : undefined}
    >
      <div className="stat-label">{label}</div>
      <div className="stat-value" style={{ color: accent }}>
        {value?.toLocaleString() ?? "—"}
      </div>
      {sub != null && <div style={{ fontSize: 12, color: "#9ca3af", marginTop: 4 }}>{sub}</div>}
    </div>
  );
}

function MyList({ initialFilter = "all" }) {
  const authUser = JSON.parse(localStorage.getItem("auth_user") || "{}");
  const isAdmin = authUser.role === "admin";
  const [users, setUsers] = useState([]);
  const [myPage, setMyPage] = useState(1);
  const MY_PAGE_SIZE = 50;
  const [selectedUser, setSelectedUser] = useState(
    isAdmin ? (localStorage.getItem("selected_user") || authUser.user_id || "777157753") : authUser.user_id
  );
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [addingId, setAddingId] = useState("");
  const [message, setMessage] = useState(null);
  const [stockFilter, setStockFilter] = useState(initialFilter);

  useEffect(() => {
    authFetch("/api/users")
      .then(r => r.json())
      .then(d => setUsers(Array.isArray(d) ? d : []))
      .catch(() => setUsers([]));
  }, []);

  const reloadList = () => {
    setLoading(true);
    authFetch(`/api/users/${selectedUser}/list`)
      .then(r => r.json())
      .then(d => setItems(d.products || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    localStorage.setItem("selected_user", selectedUser);
    reloadList();
    // eslint-disable-next-line
  }, [selectedUser]);

  useEffect(() => {
    if (!query.trim()) { setSearchResults([]); return; }
    const q = query.trim();
    const urlMatch = q.match(/\/p\/([^\/?\s]+)/);
    let searchQuery = urlMatch ? urlMatch[1] : q;

    const timer = setTimeout(() => {
      setSearching(true);
      const idPattern = /^[0-9]+(-[A-Z0-9]+)*$/i;
      const url = idPattern.test(searchQuery)
        ? `/api/catalog/product/${encodeURIComponent(searchQuery)}`
        : `/api/catalog/search?q=${encodeURIComponent(searchQuery)}&limit=20&no_tire=1&no_warehouse=1`;
      authFetch(url)
        .then(r => r.json())
        .then(d => {
          if (Array.isArray(d)) setSearchResults(d);
          else if (d.product_id) setSearchResults([d]);
          else if (d.items && Array.isArray(d.items)) setSearchResults(d.items);
          else setSearchResults([]);
        })
        .catch(() => setSearchResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const isInList = (productId) =>
    items.some(p => (p.url || "").includes(`/p/${productId}`));

  const handleAdd = (productId) => {
    setAddingId(productId);
    setMessage(null);
    authFetch(`/api/users/${selectedUser}/list/add`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ product_id: productId })
    })
      .then(r => r.json())
      .then(d => {
        if (d.message) {
          reloadList();
          setMessage({ type: "success", text: `✅ 已加入：${d.product.name}` });
        } else {
          setMessage({ type: "error", text: `❌ ${d.error || "新增失敗"}` });
        }
      })
      .catch(() => setMessage({ type: "error", text: "❌ 連線失敗" }))
      .finally(() => {
        setAddingId("");
        setTimeout(() => setMessage(null), 3000);
      });
  };

  const handleRemove = (url, name) => {
    const match = url.match(/\/p\/([^\/]+)/);
    const productId = match ? match[1] : null;
    if (!productId) return;
    if (!window.confirm(`確定要移除「${name}」嗎？`)) return;

    authFetch(`/api/users/${selectedUser}/list/remove`, {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ product_id: productId })
    })
      .then(r => r.json())
      .then(d => {
        if (d.message) setItems(items.filter(p => p.url !== url));
        else alert(d.error || "移除失敗");
      })
      .catch(() => alert("連線失敗"));
  };

  // 商品價格 + tag 顯示元件
  const renderPriceWithTag = (p) => {
    const price = p.current_price ?? p.price;
    if (p.is_warehouse_only === 1) return <span className="muted">賣場為主</span>;
    if (p.is_tire === 1) return <span className="muted">需登入</span>;
    if (!price && p.hide_price_value === 1) return <span className="muted">需登入</span>;
    if (!price) return <span className="muted">—</span>;
    return <span>NT${price.toLocaleString()}</span>;
  };

  const renderSaleTag = (p) => {
    const price = p.current_price ?? p.price;
    const isClearance = price && Math.round(price) % 10 === 7;
    if (p.has_coupon === 1) return <span style={{ padding: "2px 8px", background: "#fce7f3", color: "#9d174d", borderRadius: 4, fontSize: 11 }}>特價</span>;
    if (isClearance) return <span style={{ padding: "2px 8px", background: "#ffedd5", color: "#9a3412", borderRadius: 4, fontSize: 11 }}>出清</span>;
    return <span className="muted">—</span>;
  };

  const renderStock = (p) => {
    if (p.catalog_status === "removed") return <span className="badge red">已下架</span>;
    if (p.is_warehouse_only === 1) return <span style={{ fontSize: 12, padding: "2px 8px", background: "#fef3c7", color: "#92400e", borderRadius: 4, fontWeight: 500 }}>賣場限定</span>;
    const s = p.stock_status;
    if (s === "inStock") return <span className="badge green">有貨</span>;
    if (s === "outOfStock") return <span className="badge red">缺貨</span>;
    if (s === "lowStock") return <span className="badge yellow">少量</span>;
    return <span className="muted">—</span>;
  };

  const renderPriceDelta = (added, current) => {
    if (!added || !current) return null;
    const diff = current - added;
    if (Math.abs(diff) < 0.5) return null;
    const pct = ((diff / added) * 100).toFixed(1);
    const isDown = diff < 0;
    return (
      <span style={{ marginLeft: 6, fontSize: 11, color: isDown ? "#dc2626" : "#16a34a" }}>
        {isDown ? "▼" : "▲"} {Math.abs(pct)}%
      </span>
    );
  };

  const filteredItems = stockFilter === "all" ? items
    : stockFilter === "in_stock" ? items.filter(i => i.stock_status === "inStock" || i.stock_status === "lowStock")
    : items.filter(i => !i.is_warehouse_only && (i.stock_status === "outOfStock" || !i.stock_status));
  const myTotalPages = Math.max(1, Math.ceil(filteredItems.length / MY_PAGE_SIZE));
  const pageItems = filteredItems.slice((myPage - 1) * MY_PAGE_SIZE, myPage * MY_PAGE_SIZE);
  
  return (
    <div className="page-fill">
      <h1 className="page-title">我的監控清單</h1>

      {isAdmin && (
        <div style={{ marginBottom: 20, display: "flex", alignItems: "center", gap: 12 }}>
          <label style={{ fontSize: 14, fontWeight: 500 }}>選擇用戶：</label>
          <select
            value={selectedUser}
            onChange={e => setSelectedUser(e.target.value)}
            style={{ padding: "8px 12px", borderRadius: 6, border: "1px solid #e5e7eb", fontSize: 14, minWidth: 200 }}
          >
            {users.map(u => (
              <option key={u.user_id} value={u.user_id}>
                {u.username} ({u.product_count} 件)
              </option>
            ))}
          </select>
        </div>
      )}

      <div style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: 8, padding: 16, marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 10, color: "#374151" }}>
          ➕ 新增監控商品
        </div>
        <input
          type="text"
          placeholder="輸入商品 ID（如 148967）、完整網址、或關鍵字（如「電池」）..."
          value={query}
          onChange={e => setQuery(e.target.value)}
          style={{ width: "100%", padding: "10px 14px", border: "1px solid #d1d5db", borderRadius: 6, fontSize: 14, outline: "none", boxSizing: "border-box" }}
        />

        {message && (
          <div style={{
            marginTop: 10, padding: "8px 12px", borderRadius: 6, fontSize: 13,
            background: message.type === "success" ? "#dcfce7" : "#fee2e2",
            color: message.type === "success" ? "#166534" : "#991b1b"
          }}>
            {message.text}
          </div>
        )}

        {searching && <div style={{ marginTop: 10, color: "#6b7280", fontSize: 13 }}>搜尋中...</div>}

        {!searching && query.trim() && searchResults.length === 0 && (
          <div style={{ marginTop: 10, color: "#6b7280", fontSize: 13 }}>
            找不到符合的商品
          </div>
        )}

        {searchResults.length > 0 && (
          <div style={{ marginTop: 12, maxHeight: 360, overflowY: "auto", background: "white", border: "1px solid #e5e7eb", borderRadius: 6 }}>
            {searchResults.slice(0, 20).map(p => {
              const inList = isInList(p.product_id);
              const price = p.price;
              const isClearance = price && Math.round(price) % 10 === 7;
              return (
                <div key={p.product_id} style={{
                  padding: "10px 14px", borderBottom: "1px solid #f3f4f6",
                  display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12
                }}>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, color: "#111827", marginBottom: 4 }}>
                      {p.name}
                      {p.has_coupon === 1 && <span style={{ marginLeft: 6, padding: "2px 6px", background: "#fce7f3", color: "#9d174d", borderRadius: 4, fontSize: 11 }}>特價</span>}
                      {isClearance && <span style={{ marginLeft: 6, padding: "2px 6px", background: "#ffedd5", color: "#9a3412", borderRadius: 4, fontSize: 11 }}>出清</span>}
                      {p.is_warehouse_only === 1 && <span style={{ marginLeft: 6, padding: "2px 6px", background: "#fef3c7", color: "#92400e", borderRadius: 4, fontSize: 11 }}>賣場限定</span>}
                    </div>
                    <div style={{ fontSize: 11, color: "#9ca3af", fontFamily: "monospace" }}>
                      ID: {p.product_id}
                      {price ? ` · NT$${price.toLocaleString()}` : (p.is_warehouse_only ? " · 賣場限定" : " · 無價格")}
                    </div>
                  </div>
                  {inList ? (
                    <span style={{ fontSize: 12, color: "#9ca3af", padding: "4px 10px" }}>已加入</span>
                  ) : (
                    <button
                      onClick={() => handleAdd(p.product_id)}
                      disabled={addingId === p.product_id}
                      style={{
                        padding: "4px 12px", background: addingId === p.product_id ? "#9ca3af" : "#0056b3",
                        color: "white", border: "none", borderRadius: 4, fontSize: 12,
                        fontWeight: 600, cursor: addingId === p.product_id ? "wait" : "pointer", whiteSpace: "nowrap"
                      }}
                    >
                      {addingId === p.product_id ? "加入中..." : "+ 加入"}
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {loading && <div className="loading">載入中...</div>}

      {!loading && items.length === 0 && (
        <div className="empty-state">這位用戶還沒有監控商品</div>
      )}

      {message && (
        <div style={{ position: "fixed", top: 20, right: 20, padding: "10px 16px", borderRadius: 8, background: message.type === "success" ? "#dcfce7" : "#fee2e2", color: message.type === "success" ? "#166534" : "#991b1b", zIndex: 1000, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
          {message.text}
        </div>
      )}

      {!loading && items.length > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
          <span style={{ fontSize: 12, color: "#9ca3af", flexShrink: 0 }}>篩選</span>
          <div style={{ display: "flex", border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}>
            {[
              { key: "all",          label: "全部" },
              { key: "in_stock",     label: "有庫存", dot: "#16a34a" },
              { key: "out_of_stock", label: "缺貨中", dot: "#dc2626" },
            ].map((f, idx, arr) => (
              <button key={f.key} onClick={() => { setStockFilter(f.key); setMyPage(1); }}
                style={{
                  padding: "6px 16px", border: "none", cursor: "pointer", fontSize: 13, fontWeight: 500,
                  borderRight: idx < arr.length - 1 ? "1px solid #e5e7eb" : "none",
                  background: stockFilter === f.key ? "#111827" : "#fff",
                  color: stockFilter === f.key ? "#fff" : "#374151",
                  display: "flex", alignItems: "center", gap: 6, transition: "background 0.15s",
                }}>
                {f.dot && <span style={{ width: 7, height: 7, borderRadius: "50%", background: stockFilter === f.key ? "#fff" : f.dot, flexShrink: 0 }} />}
                {f.label}
              </button>
            ))}
          </div>
        </div>
      )}
      {!loading && items.length > 0 && (
        <div className="card card-fill">
          <div className="card-header">
            <span>共 {filteredItems.length.toLocaleString()} 件{stockFilter !== "all" ? "（篩選後）" : ""}監控商品</span>
          </div>
          <div className="table-scroll">
              <table className="table">
            <thead>
              <tr>
                <th>商品名稱</th>
                <th style={{ width: 110 }} className="left">加入價格</th>
                <th style={{ width: 130 }} className="left">現在價格</th>
                <th style={{ width: 80 }} className="center">狀態</th>
                <th style={{ width: 90 }} className="center">庫存</th>
                <th style={{ width: 130 }} className="center">加入日期</th>
                <th style={{ width: 80 }} className="center">操作</th>
              </tr>
            </thead>
            <tbody>
              {pageItems.map(p => (
                <tr key={p.url}>
                  <td>
                    <a href={p.url} target="_blank" rel="noreferrer" className="link">{p.name}</a>
                  </td>
                  <td className="left mono">
                    {p.added_price ? `NT$${p.added_price.toLocaleString()}` : <span className="muted">—</span>}
                  </td>
                  <td className="left mono">
                    {renderPriceWithTag(p)}
                    {renderPriceDelta(p.added_price, p.current_price)}
                  </td>
                  <td className="center">{renderSaleTag(p)}</td>
                  <td className="center">{renderStock(p)}</td>
                  <td className="center mono small muted" style={{ whiteSpace: "nowrap" }}>{p.added_at ? p.added_at.slice(0,10) : "—"}</td>
                  <td className="center">
                    <button
                      onClick={() => handleRemove(p.url, p.name)}
                      style={{ padding: "4px 10px", background: "#fee2e2", color: "#991b1b", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12 }}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <Pagination page={myPage} totalPages={myTotalPages} onPageChange={setMyPage} />
        </div>
      )}

      {isAdmin && (
        <KeywordAlertsCard userId={selectedUser} setMessage={setMessage} />
      )}
    </div>
  );
}

function Catalog({ onSale = false, setOnSale, onCoupon = false, setOnCoupon }) {
  const [stats, setStats] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [sort, setSort] = useState("");
  const [stockFilter, setStockFilter] = useState("");
  const [view, setView] = useState(() => { const v = window._catalogView || ""; delete window._catalogView; return v; });
  const [category, setCategory] = useState("");
  const [categories, setCategories] = useState([]);
  const [addingId, setAddingId] = useState("");
  const [addedIds, setAddedIds] = useState(new Set());
  const [catalogMsg, setCatalogMsg] = useState(null);
  const LIMIT = 50;
  const catalogAuthUser = JSON.parse(localStorage.getItem("auth_user") || "{}");
  const catalogUid = catalogAuthUser.user_id;

  useEffect(() => {
    authFetch("/api/catalog/stats").then(r => r.json()).then(setStats).catch(() => {});
    authFetch("/api/catalog/categories").then(r => r.json()).then(setCategories).catch(() => {});
  }, []);

  // 載入當前用戶清單
  useEffect(() => {
    if (!catalogUid) return;
    authFetch(`/api/users/${catalogUid}/list`)
      .then(r => r.json())
      .then(d => {
        const ids = new Set();
        (d.products || []).forEach(p => {
          const m = p.url?.match(/\/p\/([^/?]+)/);
          if (m) ids.add(m[1]);
        });
        setAddedIds(ids);
      })
      .catch(() => {});
  }, [catalogUid]);

  const handleCatalogAdd = (productId) => {
    if (!catalogUid) return;
    setAddingId(productId);
    setCatalogMsg(null);
    authFetch(`/api/users/${catalogUid}/list/add`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ product_id: productId })
    })
      .then(r => r.json())
      .then(d => {
        if (d.message) {
          setAddedIds(prev => new Set(prev).add(productId));
          setCatalogMsg({ type: "success", text: `✅ 已加入：${d.product?.name || productId}` });
        } else {
          setCatalogMsg({ type: "error", text: `❌ ${d.error || "新增失敗"}` });
        }
      })
      .catch(() => setCatalogMsg({ type: "error", text: "❌ 連線失敗" }))
      .finally(() => {
        setAddingId("");
        setTimeout(() => setCatalogMsg(null), 3000);
      });
  };

  const doSearch = (q, off = 0, sale = onSale, sortBy = sort, coupon = onCoupon, viewFilter = view, stockF = stockFilter, cat = category) => {
    setLoading(true);
    authFetch(`/api/catalog/search?q=${encodeURIComponent(q)}&limit=${LIMIT}&offset=${off}&on_sale=${sale ? 1 : 0}&on_coupon=${coupon ? 1 : 0}&sort=${sortBy}&no_tire=1&no_warehouse=1&view=${viewFilter}&stock=${stockF}&category=${encodeURIComponent(cat)}`)
      .then(r => r.json())
      .then(d => {
        setResults(d.items || []);
        setTotal(d.total || 0);
        setOffset(off);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  // 從 Dashboard 跳轉過來時，自動載入
  useEffect(() => {
    if (!onSale && !onCoupon) doSearch("", 0, false, sort, false, view);
    if (onSale) doSearch("", 0, true, sort, false, view);
  }, [onSale]);
  useEffect(() => {
    if (onCoupon) doSearch("", 0, false, sort, true, view);
  }, [onCoupon]);
  // view 切換時重新搜尋
  useEffect(() => {
    if (view) {
      setOnSale(false);
      setOnCoupon(false);
      setStockFilter("");
      doSearch(query, 0, false, sort, false, view, "", category);
    } else {
      setStockFilter("");
      doSearch(query, 0, onSale, sort, onCoupon, "", "", category);
    }
  }, [view]);

  const STOCK_CYCLE = ["", "inStock", "lowStock", "outOfStock"];
  const STOCK_LABELS = { "": "庫存 ⇅", "inStock": "✅ 有貨", "lowStock": "⚠️ 少量", "outOfStock": "❌ 缺貨" };
  const cycleStock = () => {
    const allowed = view === "hot" ? ["", "inStock", "lowStock"] : STOCK_CYCLE;
    const cur = allowed.includes(stockFilter) ? stockFilter : "";
    const next = allowed[(allowed.indexOf(cur) + 1) % allowed.length];
    setStockFilter(next);
    doSearch(query, 0, onSale, sort, onCoupon, view, next, category);
  };

  // 切換排序時重新搜尋
  const changeSort = (newSort) => {
    const next = sort === newSort ? "" : newSort;
    setSort(next);
    doSearch(query, 0, onSale, next, onCoupon, view, stockFilter, category);
  };

  return (
    <div className="page-fill">
      <h1 className="page-title">🔍 全站商品總表</h1>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {[
          { key: "", label: "📋 全部" },
          { key: "discount", label: "🔥 特價商品", count: stats?.on_coupon },
          { key: "clearance", label: "🏷️ 出清", count: stats?.on_sale },
          { key: "hot", label: "✨ 熱賣中", count: stats?.in_stock },
          { key: "low_stock", label: "⚠️ 少量", count: stats?.low_stock },
          { key: "out_of_stock", label: "📭 完售", count: stats?.out_of_stock },
          { key: "new", label: "🆕 新上架", count: stats?.total_listed },
          { key: "removed", label: "🗑️ 下架", count: stats?.total_delisted },
        ].map(f => (
          <button
            key={f.key}
            onClick={() => { setView(f.key); setOnSale(false); setOnCoupon(false); }}
            className={view === f.key ? "btn-primary" : "btn-secondary"}
            style={{ minWidth: 100 }}
          >{f.label}{f.count != null ? ` ${f.count.toLocaleString()}` : ""}</button>
        ))}
      </div>

      {categories.length > 0 && (
        <div style={{ display: "flex", gap: 6, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
          <span style={{ fontSize: 11, color: "#9ca3af", flexShrink: 0, marginRight: 2, letterSpacing: "0.04em" }}>分類</span>
          <button
            onClick={() => { setCategory(""); doSearch(query, 0, onSale, sort, onCoupon, view, stockFilter, ""); }}
            style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: "pointer", whiteSpace: "nowrap",
              border: "1px solid " + (category === "" ? "#374151" : "#e5e7eb"),
              background: category === "" ? "#374151" : "#f9fafb", color: category === "" ? "#fff" : "#6b7280" }}>全部</button>
          {categories.map(cat => (
            <button key={cat.key}
              onClick={() => { setCategory(cat.key); doSearch(query, 0, onSale, sort, onCoupon, view, stockFilter, cat.key); }}
              style={{ padding: "3px 10px", borderRadius: 20, fontSize: 12, fontWeight: category === cat.key ? 600 : 400, cursor: "pointer", whiteSpace: "nowrap",
                border: "1px solid " + (category === cat.key ? "#2563eb" : "#e5e7eb"),
                background: category === cat.key ? "#eff6ff" : "#f9fafb", color: category === cat.key ? "#2563eb" : "#6b7280" }}>
              {cat.label}<span style={{ marginLeft: 4, opacity: 0.6, fontSize: 11 }}>{cat.count.toLocaleString()}</span>
            </button>
          ))}
        </div>
      )}

      <SearchBox
        value={query}
        onChange={(val) => { setQuery(val); doSearch(val, 0, onSale, sort, onCoupon, view, stockFilter, category); }}
        placeholder="搜尋商品名稱或 ID（例：74561、沙宣）..."
      />

      {loading && <div className="loading">搜尋中...</div>}

      {catalogMsg && (
        <div style={{ position: "fixed", top: 20, right: 20, padding: "10px 16px", borderRadius: 8, background: catalogMsg.type === "success" ? "#dcfce7" : "#fee2e2", color: catalogMsg.type === "success" ? "#166534" : "#991b1b", zIndex: 1000, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
          {catalogMsg.text}
        </div>
      )}

      {!loading && results.length > 0 && (
        <div className="card card-fill">
          <div className="card-header">
            <span>找到 {total.toLocaleString()} 筆，顯示 {offset + 1}-{Math.min(offset + LIMIT, total)}</span>
          </div>
          <div className="table-scroll">
          <table className="table">
            <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
              <tr>
                <SortableTh label="商品名稱" sortKey="name" currentSort={sort} onChange={changeSort} className="center" />
                <SortableTh label="商品ID" sortKey="id" currentSort={sort} onChange={changeSort} style={{ width: 130 }} className="center" />
                {view === "discount" ? (<>
                  <th style={{ width: 110 }} className="right">原價</th>
                  <th style={{ width: 110 }} className="right">折扣</th>
                  <th style={{ width: 110 }} className="right">售價</th>
                </>) : (
                  <SortableTh label="售價" sortKey="price" currentSort={sort} onChange={changeSort} style={{ width: 130 }} className="center" />
                )}
                <th style={{ width: 100, cursor: "pointer", userSelect: "none" }} className={"center" + (stockFilter ? " th-filtered" : "")} onClick={cycleStock} title="點擊篩選庫存狀態">{STOCK_LABELS[stockFilter]}</th>
                <SortableTh label="更新時間" sortKey={view === "new" ? "time" : view === "removed" ? "last_seen" : "stock_time"} currentSort={sort} onChange={changeSort} style={{ width: 140 }} className="center" />
                <th style={{ width: 100 }} className="center">操作</th>
              </tr>
            </thead>
            <tbody>
              {results.map((p) => (
                <tr key={p.product_id}>
                  <td>
                    <a href={p.url} target="_blank" rel="noreferrer" className="link">
                      {p.name}
                    </a>
                  </td>
                  <td className="mono small center">
                    <div>{p.base_id}</div>
                    {p.product_id !== p.base_id && (
                      <div className="muted" style={{ fontSize: 10 }}>{p.product_id}</div>
                    )}
                  </td>
                  {view === "discount" ? (<>
                    <td className="mono right">
                      {p.base_price ? `NT$${Math.round(Number(p.base_price)).toLocaleString()}` : <span className="muted">—</span>}
                    </td>
                    <td className="mono right" style={{ color: "#16a34a" }}>
                      {p.base_price && p.price && p.base_price > p.price
                        ? `-NT$${Math.round(Number(p.base_price) - Number(p.price)).toLocaleString()}`
                        : <span className="muted">—</span>}
                    </td>
                    <td className="mono right" style={{ fontWeight: 600, color: "#dc2626" }}>
                      {p.price ? `NT$${Math.round(Number(p.price)).toLocaleString()}` : <span className="muted">—</span>}
                    </td>
                  </>) : (
                    <td className="mono small center">
                      {p.price ? `NT$${Math.round(Number(p.price)).toLocaleString()}` : <span className="muted">—</span>}
                    </td>
                  )}
                  <td className="center">
                    <StockBadge status={p.stock_status} added_at={p.added_at} product_status={p.status} showNew={false} />
                  </td>
                  <td className="mono small muted center">
                    {view === "new" ? (p.added_at || "—") : view === "removed" ? (p.last_seen || "—") : (p.stock_updated_at || "—")}
                  </td>
                  <td className="center">
                    {addedIds.has(p.product_id) ? (
                      <span style={{ fontSize: 12, color: "#9ca3af" }}>已加入</span>
                    ) : (
                      <button
                        onClick={() => handleCatalogAdd(p.product_id)}
                        disabled={addingId === p.product_id}
                        style={{ padding: "4px 10px", background: "#0056b3", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, whiteSpace: "nowrap" }}
                      >
                        {addingId === p.product_id ? "加入中..." : "加入追蹤"}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <Pagination page={Math.floor(offset / LIMIT) + 1} totalPages={Math.max(1, Math.ceil(total / LIMIT))} onPageChange={(p) => doSearch(query, (p - 1) * LIMIT)} /></div>
      )}

      {!loading && results.length === 0 && query && (
        <div className="empty-state">找不到符合的商品</div>
      )}

      {!loading && results.length === 0 && !query && (
        <div className="empty-state">
          <p>輸入關鍵字搜尋全站 {stats?.total?.toLocaleString() ?? ""} 件商品</p>
        </div>
      )}
    </div>
  );
}




const EVENT_META = {
  listed:    { icon: "✨", label: "新上架", color: "#1e40af", bg: "#dbeafe" },
  delisted:  { icon: "🗑️", label: "下架",  color: "#6b7280", bg: "#f3f4f6" },
  price_down:{ icon: "🔥", label: "降價",  color: "#16a34a", bg: "#dcfce7" },
  price_up:  { icon: "📈", label: "漲價",  color: "#dc2626", bg: "#fee2e2" },
  stock_in:  { icon: "🎉", label: "補貨",  color: "#16a34a", bg: "#dcfce7" },
  stock_out: { icon: "📭", label: "完售",  color: "#dc2626", bg: "#fee2e2" },
  stock_low: { icon: "⚠️", label: "少量",  color: "#92400e", bg: "#fef3c7" },
  clearance: { icon: "🏷️", label: "出清",  color: "#9a3412", bg: "#ffedd5" },
};


function StockStatusBadge({ status }) {
  if (!status) return <span className="muted small">—</span>;
  const map = {
    inStock: { label: "有貨", color: "#16a34a", bg: "#dcfce7" },
    outOfStock: { label: "缺貨", color: "#dc2626", bg: "#fee2e2" },
    lowStock: { label: "少量", color: "#92400e", bg: "#fef3c7" },
    partialStock: { label: "部分有貨", color: "#92400e", bg: "#fef3c7" },
  };
  const m = map[status] || { label: status, color: "#6b7280", bg: "#f3f4f6" };
  return (
    <span style={{
      display: "inline-block", padding: "2px 8px", background: m.bg, color: m.color,
      borderRadius: 4, fontSize: 11, fontWeight: 600, whiteSpace: "nowrap"
    }}>{m.label}</span>
  );
}

function CurrentStateCell({ event }) {
  const isDelisted = event.current_status === "removed";
  if (isDelisted) {
    return <span className="muted small">已下架</span>;
  }
  return (
    <div className="small">
      {event.current_price != null && (
        <div className="mono">NT${Math.round(Number(event.current_price)).toLocaleString()}</div>
      )}
      {event.current_stock_status && (
        <StockStatusBadge status={event.current_stock_status} />
      )}
    </div>
  );
}

function EventBadge({ type }) {
  const m = EVENT_META[type] || { icon: "❓", label: type, color: "#6b7280", bg: "#f3f4f6" };
  return (
    <span style={{
      display: "inline-block",
      padding: "3px 10px",
      background: m.bg,
      color: m.color,
      borderRadius: 4,
      fontSize: 12,
      fontWeight: 600,
      whiteSpace: "nowrap"
    }}>
      {m.icon} {m.label}
    </span>
  );
}

function EventValueChange({ event }) {
  const t = event.event_type;
  const oldV = event.old_value;
  const newV = event.new_value;
  if (t === "price_down" || t === "price_up") {
    return (
      <span className="mono small">
        NT${Math.round(Number(oldV)).toLocaleString()} → <strong style={{ color: t === "price_down" ? "#16a34a" : "#dc2626" }}>NT${Math.round(Number(newV)).toLocaleString()}</strong>
      </span>
    );
  }
  if (t === "stock_in" || t === "stock_out") {
    const displayNew = (t === "stock_out" && event.current_stock_status === "partialStock") ? "部分有貨" : newV;
    return <span className="small">{oldV} → <strong>{displayNew}</strong></span>;
  }
  if (t === "listed") {
    return <span className="small muted">{newV ? `NT$${Math.round(Number(newV)).toLocaleString()}` : "—"}</span>;
  }
  if (t === "delisted") {
    return <span className="small muted">最後價 {oldV ? `NT$${Math.round(Number(oldV)).toLocaleString()}` : "—"}</span>;
  }
  return null;
}

function EventsPage({ onSelectProduct }) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState(() => {
    // 優先從 URL 參數讀取 type，否則從 window._eventsFilter 讀取
    const params = new URLSearchParams(window.location.search);
    const typeParam = params.get("type");
    if (typeParam) return typeParam;
    
    const f = window._eventsFilter || "";
    delete window._eventsFilter;
    return f;
  });
  const [q, setQ] = useState("");
  const { addingId, addedIds, message, handleAdd } = useAddToList();
  const PAGE_SIZE = 50;

  useEffect(() => {
    setLoading(true);
    authFetch(`/api/catalog/events?event_type=${filter}&page=${page}&page_size=${PAGE_SIZE}&q=${encodeURIComponent(q)}`)
      .then(r => r.json())
      .then(d => {
        setItems(d.items || []);
        setTotalPages(d.total_pages || 1);
        setTotal(d.total || 0);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [page, filter, q]);

  useEffect(() => { setPage(1); }, [filter, q]);

  const filters = [
    { key: "", label: "📋 全部" },
    { key: "price_down", label: "🔥 降價" },
    { key: "stock_in", label: "🎉 補貨" },
    { key: "listed", label: "✨ 新上架" },
    { key: "stock_low", label: "⚠️ 少量" },
    { key: "stock_out", label: "📭 完售" },
    { key: "clearance", label: "🏷️ 出清" },
    { key: "price_up", label: "📈 漲價" },
    { key: "delisted", label: "🗑️ 下架" },
  ];

  return (
    <div className="page-fill">
      <h1 className="page-title">📰 商品最新動態</h1>

      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        {filters.map(f => (
          <button
            key={f.key}
            onClick={() => setFilter(f.key)}
            className={filter === f.key ? "btn-primary" : "btn-secondary"}
            style={{ minWidth: 100 }}
          >{f.label}</button>
        ))}
      </div>

      <SearchBox value={q} onChange={setQ} />

      {loading && <div className="loading">載入中...</div>}
      {!loading && items.length === 0 && (
        <div className="empty-state">目前無事件{q && `（含「${q}」）`}</div>
      )}

      {!loading && items.length > 0 && (
        <div className="card card-fill">
          <div className="card-header">
            <span>共 {total.toLocaleString()} 筆事件</span>
          </div>
          <div className="table-scroll">
            <table className="table">
              <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                <tr>
                  <th style={{ width: 100 }} className="center">狀態</th>
                  <th className="center">商品名稱</th>
                  <th style={{ width: 140 }} className="center">商品ID</th>
                  <th style={{ width: 200 }} className="center">變化</th>
                  <th style={{ width: 140 }} className="center">商品現況</th>
                  <th style={{ width: 160 }} className="center">時間</th>
                  <th style={{ width: 130 }} className="center">操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map(e => (
                  <tr key={e.id}>
                    <td className="center"><EventBadge type={e.event_type} /></td>
                    <td>
                      <a href={e.url} target="_blank" rel="noreferrer" className="link product-name" title={e.name}>{e.name}</a>
                    </td>
                    <td className="mono small center">{e.product_id}</td>
                    <td className="center"><EventValueChange event={e} /></td>
                    <td className="center"><CurrentStateCell event={e} /></td>
                    <td className="mono small muted center">{e.occurred_at ? e.occurred_at.replace(/\.\d+$/, '') : '—'}</td>
                    <td className="center">
                      {e.event_type === "delisted"
                        ? <span className="muted small">—</span>
                        : <AddButton productId={e.product_id} addingId={addingId} addedIds={addedIds} onAdd={handleAdd} />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}


function SettingsPage() {
  const authUser = JSON.parse(localStorage.getItem("auth_user") || "{}");
  const uid = authUser.user_id;
  const [prefs, setPrefs] = useState({ notify_new_products: true, notify_price_drops: true });
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    if (!uid) return;
    authFetch(`/api/users/${uid}/preferences`)
      .then(r => r.json())
      .then(d => { if (!d.error) setPrefs(d); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [uid]);

  const toggle = (key) => {
    const newVal = !prefs[key];
    setPrefs({ ...prefs, [key]: newVal });
    authFetch(`/api/users/${uid}/preferences`, {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ [key]: newVal })
    })
      .then(r => r.json())
      .then(d => {
        if (d.error) {
          setMessage({ type: "error", text: `❌ ${d.error}` });
          setPrefs(prefs);
        } else {
          setMessage({ type: "success", text: "✅ 已儲存" });
        }
      })
      .catch(() => {
        setMessage({ type: "error", text: "❌ 連線失敗" });
        setPrefs(prefs);
      })
      .finally(() => setTimeout(() => setMessage(null), 2500));
  };

  const Toggle = ({ value, onChange }) => (
    <button onClick={onChange} style={{
      position: "relative", width: 56, height: 28, borderRadius: 14,
      background: value ? "#22c55e" : "#d1d5db", border: "none", cursor: "pointer", transition: "background 0.2s"
    }}>
      <span style={{
        position: "absolute", top: 2, left: value ? 30 : 2, width: 24, height: 24, borderRadius: 12,
        background: "#fff", transition: "left 0.2s", boxShadow: "0 2px 4px rgba(0,0,0,0.2)"
      }} />
    </button>
  );

  if (loading) return <div className="page"><div className="loading">載入中...</div></div>;

  return (
    <div className="page">
      <h1 className="page-title">⚙️ 我的設定</h1>
      <MessageToast message={message} />

      <div className="card" style={{ padding: 24, maxWidth: 600 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, marginTop: 0, marginBottom: 20, color: "#374151" }}>
          📢 Telegram 通知偏好
        </h2>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0", borderBottom: "1px solid #f3f4f6" }}>
          <div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>🆕 新上架通知</div>
            <div style={{ fontSize: 13, color: "#6b7280" }}>偵測到全新商品上架時推送</div>
          </div>
          <Toggle value={prefs.notify_new_products} onChange={() => toggle("notify_new_products")} />
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 0" }}>
          <div>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>📉 全站降價通知</div>
            <div style={{ fontSize: 13, color: "#6b7280" }}>每次價格全站更新偵測到降價時推送</div>
          </div>
          <Toggle value={prefs.notify_price_drops} onChange={() => toggle("notify_price_drops")} />
        </div>
      </div>

      <KeywordAlertsCard userId={uid} setMessage={setMessage} />
    </div>
  );
}


function KeywordAlertsCard({ userId, setMessage }) {
  const authUser = JSON.parse(localStorage.getItem("auth_user") || "{}");
  const isAdmin = authUser.role === "admin";
  const [alerts, setAlerts] = useState([]);
  const [limit, setLimit] = useState(3);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [keyword, setKeyword] = useState("");
  const [expectedDate, setExpectedDate] = useState("");

  const load = () => {
    setLoading(true);
    Promise.all([
      authFetch(`/api/users/${userId}/keyword-alerts`).then(r => r.json()),
      authFetch(`/api/users/${userId}/preferences`).then(r => r.json()),
    ])
      .then(([alertsData, prefsData]) => {
        setAlerts(Array.isArray(alertsData) ? alertsData : []);
        if (prefsData && !prefsData.error) setLimit(prefsData.keyword_alert_limit ?? 3);
      })
      .catch(() => setAlerts([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { if (userId) load(); }, [userId]);

  const handleAdd = () => {
    if (!keyword.trim() || !expectedDate) {
      setMessage({ type: "error", text: "❌ 關鍵字與日期都必填" });
      setTimeout(() => setMessage(null), 2500);
      return;
    }
    authFetch(`/api/users/${userId}/keyword-alerts`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ keyword: keyword.trim(), expected_date: expectedDate })
    })
      .then(r => r.json())
      .then(d => {
        if (d.error) setMessage({ type: "error", text: `❌ ${d.error}` });
        else {
          setMessage({ type: "success", text: "✅ 已新增" });
          setKeyword(""); setExpectedDate(""); setShowAdd(false); load();
        }
        setTimeout(() => setMessage(null), 2500);
      });
  };

  const handleDelete = (alertId) => {
    if (!window.confirm("確定刪除這個快訊？")) return;
    authFetch(`/api/users/${userId}/keyword-alerts/${alertId}`, { method: "DELETE" })
      .then(r => r.json())
      .then(d => {
        if (d.error) setMessage({ type: "error", text: `❌ ${d.error}` });
        else { setMessage({ type: "success", text: "✅ 已刪除" }); load(); }
        setTimeout(() => setMessage(null), 2500);
      });
  };

  const handleReset = (alertId) => {
    authFetch(`/api/users/${userId}/keyword-alerts/${alertId}/reset`, { method: "POST" })
      .then(r => r.json())
      .then(d => {
        if (d.error) setMessage({ type: "error", text: `❌ ${d.error}` });
        else { setMessage({ type: "success", text: "✅ 已重設等待中" }); load(); }
        setTimeout(() => setMessage(null), 2500);
      });
  };

  return (
    <div className="card" style={{ padding: 24, maxWidth: 600, marginTop: 16 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0, color: "#374151" }}>🎯 關鍵字快訊</h2>
        <button onClick={() => setShowAdd(!showAdd)} disabled={!isAdmin && alerts.length >= limit} style={{
          padding: "6px 14px", background: (!isAdmin && alerts.length >= limit) ? "#e5e7eb" : "#0056b3",
          color: (!isAdmin && alerts.length >= limit) ? "#9ca3af" : "white", border: "none", borderRadius: 4,
          cursor: (!isAdmin && alerts.length >= limit) ? "not-allowed" : "pointer", fontSize: 13
        }}>{showAdd ? "✕ 取消" : "+ 新增"}</button>
      </div>
      <p style={{ fontSize: 12, color: "#6b7280", marginTop: 0, marginBottom: 16 }}>
        預期上架日 08:00–12:00 每 2 分鐘掃描，命中時立即推送
        （目前 {alerts.length}/{isAdmin ? "∞" : limit} 個）
      </p>

      {showAdd && (
        <div style={{ background: "#f9fafb", padding: 16, borderRadius: 6, marginBottom: 16 }}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>關鍵字</label>
            <input type="text" value={keyword} onChange={e => setKeyword(e.target.value)}
              placeholder="iPhone 18 太空黑 512GB（空格分隔，最多 5 詞）"
              style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 4, boxSizing: "border-box" }} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 13, fontWeight: 500, display: "block", marginBottom: 4 }}>預期上架日期</label>
            <input type="date" value={expectedDate} onChange={e => setExpectedDate(e.target.value)}
              style={{ width: "100%", padding: "8px 12px", border: "1px solid #d1d5db", borderRadius: 4, boxSizing: "border-box" }} />
          </div>
          <button onClick={handleAdd} className="btn-primary" style={{ width: "100%" }}>🎯 新增快訊</button>
        </div>
      )}

      {loading && <div style={{ color: "#6b7280", fontSize: 13 }}>載入中...</div>}
      {!loading && alerts.length === 0 && (
        <div style={{ color: "#9ca3af", fontSize: 13, padding: "8px 0" }}>尚無關鍵字快訊</div>
      )}

      {!loading && alerts.map(a => (
        <div key={a.id} style={{
          padding: "12px 0", borderTop: "1px solid #f3f4f6",
          display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12
        }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 500, marginBottom: 4 }}>🔍 {a.keyword}</div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              預期日期：{a.expected_date}
              {" · "}
              {a.status === "waiting"
                ? <span style={{ color: "#0056b3" }}>⏳ 等待中</span>
                : <span style={{ color: "#16a34a" }}>✅ 已命中（{a.hit_at}）</span>}
            </div>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {a.status === "hit" && (
              <button onClick={() => handleReset(a.id)} style={{
                padding: "4px 10px", background: "#fef3c7", color: "#92400e",
                border: "1px solid #fde68a", borderRadius: 4, cursor: "pointer", fontSize: 12
              }}>🔄 重設</button>
            )}
            <button onClick={() => handleDelete(a.id)} style={{
              padding: "4px 10px", background: "#fee2e2", color: "#991b1b",
              border: "1px solid #fecaca", borderRadius: 4, cursor: "pointer", fontSize: 12
            }}>🗑️ 刪除</button>
          </div>
        </div>
      ))}
    </div>
  );
}


function SystemPage() {
  const [status, setStatus] = useState(null);
  const [events, setEvents] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [updating, setUpdating] = useState(false);
  const [forceLoggingOut, setForceLoggingOut] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [summaryEvents, setSummaryEvents] = useState([]);
  const PAGE_SIZE = 50;

  const loadStatus = () => {
    authFetch("/api/system/status").then(r => r.json()).then(setStatus).catch(() => {});
  };

  const loadSummary = () => {
    authFetch("/api/system/events?page=1&page_size=30")
      .then(r => r.json())
      .then(d => setSummaryEvents(d.items || []))
      .catch(() => {});
  };

  const loadEvents = (p = page, ft = filterType) => {
    const isError = ft === "__error__";
    const typeParam = (!isError && ft !== "all") ? `&event_type=${ft}` : "";
    const statusParam = isError ? "&status=error" : "";
    authFetch(`/api/system/events?page=${p}&page_size=${PAGE_SIZE}${typeParam}${statusParam}`)
      .then(r => r.json())
      .then(d => {
        setEvents(d.items || []);
        setTotalPages(d.total_pages || 1);
        setTotal(d.total || 0);
      })
      .catch(() => {});
  };

  // 用 ref 捕捉最新的 page/filterType 給 setInterval 用
  const pageRef = useRef(1);
  const filterRef = useRef("all");
  useEffect(() => { pageRef.current = page; }, [page]);
  useEffect(() => { filterRef.current = filterType; }, [filterType]);

  useEffect(() => {
    loadStatus();
    loadSummary();
    loadEvents(1, filterType);
    const interval = setInterval(() => {
      loadStatus();
      loadSummary();
      // 只在第 1 頁時自動 reload，避免翻頁時資料跳動
      if (pageRef.current === 1) {
        loadEvents(1, filterRef.current);
      }
    }, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    loadEvents(page, filterType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, filterType]);

  const handleFilterChange = (newType) => {
    setFilterType(newType);
    setPage(1);
  };

  const handleSitemapUpdate = async () => {
    setUpdating(true);
    try {
      await authFetch("/api/system/full-sync?source=web", { method: "POST" });
      setTimeout(() => { loadStatus(); loadSummary(); loadEvents(1, filterType); setUpdating(false); }, 2000);
    } catch {
      setUpdating(false);
    }
  };

  const serviceLabel = {
    "com.costco.monitor-bot": "Telegram Bot",
    "com.costco.web-api": "Web API",
    "com.costco.react": "前端介面",
  };

  const eventIcon = {
  "monitor_check": "📱",
    sitemap_update: "🔄",
    crawler: "🕷️",
    init: "✨",
    error: "❌",
    user_login: "👤",
  };

  return (
    <div className="page-fill">
      <h1 className="page-title" style={{ marginBottom: 16 }}>系統管理</h1>

      {/* ── Compact top panel ── */}
      <div className="card sysinfo-panel" style={{ padding: "14px 20px", marginBottom: 12, display: "flex", gap: 0, flexWrap: "wrap" }}>

        {/* 服務狀態 */}
        <div className="sysinfo-col" style={{ paddingRight: 24, marginRight: 24, borderRight: "1px solid #f3f4f6" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", letterSpacing: "0.05em", marginBottom: 8, textTransform: "uppercase" }}>服務狀態</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {status?.services && Object.entries(status.services).map(([key, val]) => (
              <div key={key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
                <span style={{ fontSize: 9, color: val === "running" ? "#22c55e" : "#ef4444" }}>●</span>
                <span style={{ color: "#374151" }}>{serviceLabel[key] || key}</span>
                <span style={{ fontSize: 11, color: val === "running" ? "#16a34a" : "#dc2626" }}>
                  {val === "running" ? "運行中" : "已停止"}
                </span>
              </div>
            ))}
            {status?.catalog_count != null && (
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>
                全站商品 <strong style={{ color: "#0056b3" }}>{status.catalog_count.toLocaleString()}</strong> 件
              </div>
            )}
          </div>
        </div>

        {/* 手動操作 */}
        <div className="sysinfo-col" style={{ paddingRight: 24, marginRight: 24, borderRight: "1px solid #f3f4f6" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", letterSpacing: "0.05em", marginBottom: 8, textTransform: "uppercase" }}>手動操作</div>
          {status?.last_sitemap_update && (
            <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 8 }}>上次：{status.last_sitemap_update}</div>
          )}
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          <button className="btn-primary" onClick={handleSitemapUpdate} disabled={updating}
            style={{ padding: "6px 14px", fontSize: 13, borderRadius: 8 }}>
            {updating ? "🔄 更新中..." : "🔄 立即同步"}
          </button>
          <button
            onClick={async () => {
              if (!window.confirm("確定要強制登出所有使用者嗎？\n所有人下次開啟頁面都需重新登入。")) return;
              setForceLoggingOut(true);
              try {
                const r = await authFetch("/api/auth/force-logout-all", { method: "POST" });
                if (r.ok) alert("✅ 已強制登出所有使用者");
                else alert("❌ 操作失敗");
              } catch { alert("❌ 請求失敗"); }
              finally { setForceLoggingOut(false); }
            }}
            disabled={forceLoggingOut}
            style={{ padding: "6px 14px", fontSize: 13, borderRadius: 8,
              background: "#ef4444", color: "#fff", border: "none", cursor: "pointer",
              opacity: forceLoggingOut ? 0.6 : 1 }}>
            {forceLoggingOut ? "處理中..." : "🚪 強制登出所有人"}
          </button>
          </div>
        </div>

        {/* 最近同步結果 */}
        <div className="sysinfo-col" style={{ flex: 1, minWidth: 280 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "#9ca3af", letterSpacing: "0.05em", marginBottom: 8, textTransform: "uppercase" }}>最近同步結果</div>
          <SyncSummaryCompact events={summaryEvents} />
        </div>
      </div>

      {/* ── 系統事件日誌 ── */}
      <div style={{ fontSize: 13, fontWeight: 600, color: "#6b7280", marginBottom: 8 }}>系統事件日誌</div>

      {/* 篩選按鈕 */}
      <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap" }}>
        {[
          ["all", "📋 全部"],
          ["sitemap_update", "🔄 商品同步"],
          ["crawler", "🕷 爬蟲"],
          ["stock_updater", "📦 庫存"],
          ["monitor_check", "📱 Telegram"],
          ["__error__", "❌ 失敗"],
          ["user_login", "👤 登入紀錄"],
        ].map(([k, label]) => (
          <button
            key={k}
            onClick={() => handleFilterChange(k)}
            className={filterType === k ? "btn-primary" : "btn-secondary"}
            style={{ minWidth: 120 }}
          >
            {label}
          </button>
        ))}
        <span style={{ marginLeft: "auto", color: "#6b7280", fontSize: 13, alignSelf: "center" }}>
          共 {total.toLocaleString()} 筆（保留 10 天）
        </span>
      </div>

      <div className="card card-fill">
        <div className="table-scroll">
        <table className="table" style={{ tableLayout: "fixed", width: "100%" }}>
          <thead>
            <tr>
              <th style={{ width: 50 }}></th>
              <th style={{ width: 240 }}>事件</th>
              <th>說明</th>
              <th style={{ width: 100 }}>狀態</th>
              <th style={{ width: 180 }}>時間</th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && (
              <tr><td colSpan={5} className="center muted" style={{ padding: 30 }}>尚無事件</td></tr>
            )}
            {events.map(e => (
              <tr key={e.id}>
                <td style={{ fontSize: 18 }}>{eventIcon[e.event_type] || "📌"}</td>
                <td style={{ fontWeight: 500 }}>{e.title}</td>
                <td className="muted small" style={{ whiteSpace: "pre-line" }}>{e.detail || "—"}</td>
                <td>
                  <span className={`badge ${e.status === "success" ? "green" : "red"}`}>
                    {e.status === "success" ? "成功" : e.status === "error" ? "失敗" : e.status}
                  </span>
                </td>
                <td className="mono small muted">{e.created_at}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
        {totalPages > 1 && (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, padding: 16, borderTop: "1px solid #f3f4f6" }}>
            <button
              className="btn-secondary"
              onClick={() => setPage(1)}
              disabled={page === 1}
            >« 首頁</button>
            <button
              className="btn-secondary"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >‹ 上一頁</button>
            <span style={{ fontSize: 14, color: "#374151", minWidth: 100, textAlign: "center" }}>
              第 {page} / {totalPages} 頁
            </span>
            <button
              className="btn-secondary"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
            >下一頁 ›</button>
            <button
              className="btn-secondary"
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
            >末頁 »</button>
          </div>
        )}
      </div>
    </div>
  );
}



function SyncSummaryCompact({ events }) {
  const find = (type, titleKw) => events.find(e =>
    e.event_type === type && (!titleKw || e.title.includes(titleKw))
  );
  const sitemapDone = find("sitemap_update", "同步完成");
  const crawlerDone = find("crawler", "新商品爬取完成");
  const stockDone = find("stock_updater", "庫存及價格全站更新完成");
  const telegramCheck = find("monitor_check", "");
  const p = (d, re) => { const m = d?.match(re); return m ? parseInt(m[1]) : 0; };
  const sm = { added: p(sitemapDone?.detail, /新增\s*(\d+)/), removed: p(sitemapDone?.detail, /下架\s*(\d+)/) };
  const cr = { ok: p(crawlerDone?.detail, /成功\s*(\d+)/), fail: p(crawlerDone?.detail, /失敗\s*(\d+)/) };
  const st = stockDone ? {
    inStock: p(stockDone.detail, /有\s*(\d+)/), outOfStock: p(stockDone.detail, /缺\s*(\d+)/),
    priceDown: p(stockDone.detail, /降價\s*(\d+)/) || p(stockDone.detail, /變價\s*(\d+)/),
    priceUp: p(stockDone.detail, /漲價\s*(\d+)/),
  } : null;
  const Row = ({ icon, label, time, children }) => (
    <div style={{ display: "flex", alignItems: "baseline", gap: 6, fontSize: 12, color: "#374151" }}>
      <span style={{ flexShrink: 0 }}>{icon}</span>
      <span style={{ color: "#9ca3af", flexShrink: 0 }}>{label}</span>
      {children || <span style={{ color: "#d1d5db" }}>—</span>}
      {time && <span style={{ color: "#d1d5db", marginLeft: "auto", fontSize: 11, whiteSpace: "nowrap" }}>{time.slice(5,16)}</span>}
    </div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
      <Row icon="🗺️" label="商品同步" time={sitemapDone?.created_at}>
        {sitemapDone && <span>+<strong style={{ color: "#16a34a" }}>{sm.added}</strong> / <strong style={{ color: "#dc2626" }}>-{sm.removed}</strong></span>}
      </Row>
      <Row icon="🕷️" label="新品爬取" time={crawlerDone?.created_at}>
        {crawlerDone && <span>成功 <strong style={{ color: "#16a34a" }}>{cr.ok}</strong> / 失敗 <strong style={{ color: "#dc2626" }}>{cr.fail}</strong></span>}
      </Row>
      <Row icon="📦" label="庫存更新" time={stockDone?.created_at}>
        {st && <span>有 <strong>{st.inStock.toLocaleString()}</strong> 缺 <strong>{st.outOfStock.toLocaleString()}</strong> 降 <strong style={{ color: "#16a34a" }}>{st.priceDown.toLocaleString()}</strong> 漲 <strong style={{ color: "#dc2626" }}>{st.priceUp.toLocaleString()}</strong></span>}
      </Row>
      <Row icon="📱" label="Telegram" time={telegramCheck?.created_at}>
        {telegramCheck && <span style={{ color: "#6b7280" }}>{telegramCheck.detail || telegramCheck.title}</span>}
      </Row>
    </div>
  );
}

function SyncSummary({ events }) {
  const find = (typeMatch, titleMatch) => events.find(e =>
    (!typeMatch || e.event_type === typeMatch) &&
    (!titleMatch || e.title.includes(titleMatch))
  );

  const sitemapDone = find("sitemap_update", "同步完成");
  const crawlerDone = find("crawler", "新商品爬取完成");
  const stockDone = find("stock_updater", "庫存及價格全站更新完成");
  const telegramCheck = find("monitor_check", "");

  const parseSitemap = (d) => {
    if (!d) return { added: 0, removed: 0 };
    const m1 = d.match(/新增\s*(\d+)/);
    const m2 = d.match(/下架\s*(\d+)/);
    return { added: m1 ? parseInt(m1[1]) : 0, removed: m2 ? parseInt(m2[1]) : 0 };
  };
  const parseCrawler = (d) => {
    if (!d) return { ok: 0, fail: 0 };
    const m1 = d.match(/成功\s*(\d+)/);
    const m2 = d.match(/失敗\s*(\d+)/);
    return { ok: m1 ? parseInt(m1[1]) : 0, fail: m2 ? parseInt(m2[1]) : 0 };
  };
  const parseStock = (d) => {
    if (!d) return null;
    const m1 = d.match(/有\s*(\d+)/);
    const m2 = d.match(/缺\s*(\d+)/);
    const m3 = d.match(/少\s*(\d+)/);
    const m4 = d.match(/降價\s*(\d+)/);
    const m5 = d.match(/漲價\s*(\d+)/);
    const m6 = d.match(/變價\s*(\d+)/);  // 舊版相容
    return {
      inStock: m1 ? parseInt(m1[1]) : 0,
      outOfStock: m2 ? parseInt(m2[1]) : 0,
      lowStock: m3 ? parseInt(m3[1]) : 0,
      priceDown: m4 ? parseInt(m4[1]) : 0,
      priceUp: m5 ? parseInt(m5[1]) : 0,
      priceChanged: m6 ? parseInt(m6[1]) : 0,
    };
  };

  const sm = parseSitemap(sitemapDone?.detail);
  const cr = parseCrawler(crawlerDone?.detail);
  const st = parseStock(stockDone?.detail);

  // 使用 stat-card 統一樣式
  const Card = ({ icon, title, time, body, accent }) => (
    <div className="stat-card" style={{ borderLeft: `4px solid ${accent}` }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 4 }}>
        <span style={{ fontSize: 16 }}>{icon}</span>
        <span className="stat-label" style={{ marginBottom: 0 }}>{title}</span>
      </div>
      <div style={{ fontSize: 11, color: "#9ca3af", marginBottom: 6 }}>
        {time || "尚未執行"}
      </div>
      <div style={{ fontSize: 13, color: "#1f2937", lineHeight: 1.5 }}>
        {body}
      </div>
    </div>
  );

  return (
    <div className="stats-grid">
      <Card
        icon="🗺"
        title="商品同步比對"
        time={sitemapDone?.created_at}
        accent="#0ea5e9"
        body={sitemapDone ? (
          <>
            新上架 <strong style={{ color: "#16a34a" }}>{sm.added}</strong> 件<br/>
            下架 <strong style={{ color: "#dc2626" }}>{sm.removed}</strong> 件
          </>
        ) : <span style={{ color: "#9ca3af" }}>—</span>}
      />
      <Card
        icon="🕷"
        title="新品爬取"
        time={crawlerDone?.created_at}
        accent="#8b5cf6"
        body={crawlerDone ? (
          <>
            成功 <strong style={{ color: "#16a34a" }}>{cr.ok}</strong> 件<br/>
            失敗 <strong style={{ color: "#dc2626" }}>{cr.fail}</strong> 件
          </>
        ) : <span style={{ color: "#9ca3af" }}>—</span>}
      />
      <Card
        icon="📦"
        title="庫存 / 價格全站更新"
        time={stockDone?.created_at}
        accent="#22c55e"
        body={st ? (
          <>
            有貨 <strong style={{ color: "#16a34a" }}>{st.inStock.toLocaleString()}</strong>、
            缺貨 <strong style={{ color: "#ef4444" }}>{st.outOfStock.toLocaleString()}</strong>、
            少量 <strong style={{ color: "#f59e0b" }}>{st.lowStock.toLocaleString()}</strong><br/>
            降價 <strong style={{ color: "#16a34a" }}>{(st.priceDown || st.priceChanged || 0).toLocaleString()}</strong>、
            漲價 <strong style={{ color: "#dc2626" }}>{(st.priceUp || 0).toLocaleString()}</strong>
          </>
        ) : <span style={{ color: "#9ca3af" }}>—</span>}
      />
      <Card
        icon="📱"
        title="Telegram 監控"
        time={telegramCheck?.created_at}
        accent="#0056b3"
        body={telegramCheck ? (
          <span style={{ color: "#374151", fontSize: 12 }}>{telegramCheck.detail || telegramCheck.title}</span>
        ) : <span style={{ color: "#9ca3af" }}>—</span>}
      />
    </div>
  );
}

function NewProducts() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("");
  const [q, setQ] = useState("");
  const { addingId, addedIds, message, handleAdd } = useAddToList();
  const PAGE_SIZE = 50;

  useEffect(() => {
    setLoading(true);
    authFetch(`/api/catalog/new?days=0&page=${page}&page_size=${PAGE_SIZE}&sort=${sort}&q=${encodeURIComponent(q)}&no_tire=1&no_warehouse=1`)
      .then(r => r.json())
      .then(d => {
        setItems(d.items || []);
        setTotalPages(d.total_pages || 1);
        setTotal(d.total || 0);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [page, sort, q]);

  useEffect(() => { setPage(1); }, [sort, q]);

  return (
    <div className="page-fill">
      <h1 className="page-title">✨ 最新上架</h1>
      <SearchBox value={q} onChange={setQ} />
      <MessageToast message={message} />

      {loading && <div className="loading">載入中...</div>}
      {!loading && items.length === 0 && (
        <div className="empty-state">目前無新上架商品{q && `（含「${q}」）`}</div>
      )}

      {!loading && items.length > 0 && (
        <div className="card card-fill">
          <div className="card-header">
            <span>共 {total.toLocaleString()} 件新上架商品</span>
          </div>
          <div className="table-scroll">
            <table className="table">
              <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                <tr>
                  <SortableTh label="商品名稱" sortKey="name" currentSort={sort} onChange={setSort} />
                  <SortableTh label="商品ID" sortKey="id" currentSort={sort} onChange={setSort} style={{ width: 130 }} className="center" />
                  <SortableTh label="價格" sortKey="price" currentSort={sort} onChange={setSort} style={{ width: 130 }} className="right" />
                  <SortableTh label="上架時間" sortKey="time" currentSort={sort} onChange={setSort} style={{ width: 160 }} />
                  <th style={{ width: 130 }} className="center">操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map(p => (
                  <tr key={p.product_id}>
                    <td>
                      <a href={p.url} target="_blank" rel="noreferrer" className="link product-name" title={p.name}>{p.name}</a>
                    </td>
                    <td className="mono small center">{p.base_id}</td>
                    <td className="mono right">
                      {p.is_warehouse_only === 1 ? <span className="muted">賣場為主</span> : (p.is_tire === 1 ? <span className="muted">需登入</span> : (!p.price && p.hide_price_value === 1 ? <span className="muted">需登入</span> : (p.price ? `NT$${p.price.toLocaleString()}` : <span className="muted">—</span>)))}
                    </td>
                    <td className="mono small muted">{p.added_at}</td>
                    <td className="center">
                      <AddButton productId={p.product_id} addingId={addingId} addedIds={addedIds} onAdd={handleAdd} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}

// ===== 共用：價格變動（降價/漲價/出清通用基底） =====
function useAddToList() {
  const [addingId, setAddingId] = useState("");
  const [addedIds, setAddedIds] = useState(new Set());
  const [message, setMessage] = useState(null);
  const authUser = JSON.parse(localStorage.getItem("auth_user") || "{}");
  const currentUid = authUser.user_id;

  useEffect(() => {
    if (!currentUid) return;
    authFetch(`/api/users/${currentUid}/list`)
      .then(r => r.json())
      .then(d => {
        const ids = new Set();
        (d.products || []).forEach(p => {
          const m = p.url?.match(/\/p\/([^/?]+)/);
          if (m) ids.add(m[1]);
        });
        setAddedIds(ids);
      })
      .catch(() => {});
  }, [currentUid]);

  const handleAdd = (productId) => {
    if (!currentUid) return;
    setAddingId(productId);
    setMessage(null);
    authFetch(`/api/users/${currentUid}/list/add`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ product_id: productId })
    })
      .then(r => r.json())
      .then(d => {
        if (d.message) {
          setAddedIds(prev => new Set(prev).add(productId));
          setMessage({ type: "success", text: `✅ 已加入：${d.product?.name || productId}` });
        } else {
          setMessage({ type: "error", text: `❌ ${d.error || "新增失敗"}` });
        }
      })
      .catch(() => setMessage({ type: "error", text: "❌ 連線失敗" }))
      .finally(() => {
        setAddingId("");
        setTimeout(() => setMessage(null), 3000);
      });
  };

  return { addingId, addedIds, message, handleAdd };
}

function DayFilter({ days, setDays }) {
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
      {[1, 3, 7, 14, 30].map(d => (
        <button
          key={d}
          className={d === days ? "btn-primary" : "btn-secondary"}
          onClick={() => setDays(d)}
        >
          最近 {d} 天
        </button>
      ))}
    </div>
  );
}

function AddButton({ productId, addingId, addedIds, onAdd }) {
  if (addedIds.has(productId)) {
    return <span style={{ fontSize: 12, color: "#9ca3af" }}>已加入</span>;
  }
  return (
    <button
      onClick={() => onAdd(productId)}
      disabled={addingId === productId}
      style={{ padding: "4px 12px", background: "#0056b3", color: "white", border: "none", borderRadius: 4, cursor: "pointer", fontSize: 12, whiteSpace: "nowrap" }}
    >
      {addingId === productId ? "加入中..." : "加入追蹤"}
    </button>
  );
}

function MessageToast({ message }) {
  if (!message) return null;
  return (
    <div style={{ position: "fixed", top: 20, right: 20, padding: "10px 16px", borderRadius: 8, background: message.type === "success" ? "#dcfce7" : "#fee2e2", color: message.type === "success" ? "#166534" : "#991b1b", zIndex: 1000, boxShadow: "0 4px 12px rgba(0,0,0,0.1)" }}>
      {message.text}
    </div>
  );
}

// ===== 最新降價 / 漲價 =====
function LowStock() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("");
  const [q, setQ] = useState("");
  const { addingId, addedIds, message, handleAdd } = useAddToList();
  const PAGE_SIZE = 50;

  useEffect(() => {
    setLoading(true);
    authFetch(`/api/catalog/low-stock?page=${page}&page_size=${PAGE_SIZE}&sort=${sort}&q=${encodeURIComponent(q)}&no_tire=1`)
      .then(r => r.json())
      .then(d => {
        setItems(d.items || []);
        setTotalPages(d.total_pages || 1);
        setTotal(d.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, sort, q]);

  useEffect(() => { setPage(1); }, [sort, q]);

  return (
    <div className="page-fill">
      <h1 className="page-title">⚠️ 少量庫存</h1>
      <SearchBox value={q} onChange={setQ} />
      <MessageToast message={message} />

      {loading && <div className="loading">載入中...</div>}
      {!loading && items.length === 0 && (
        <div className="empty-state">目前無少量庫存商品{q && `（含「${q}」）`}</div>
      )}

      {!loading && items.length > 0 && (
        <div className="card card-fill">
          <div className="card-header">
            <span>共 {total.toLocaleString()} 件少量庫存</span>
          </div>
          <div className="table-scroll">
            <table className="table">
              <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                <tr>
                  <SortableTh label="商品名稱" sortKey="name" currentSort={sort} onChange={setSort} />
                  <SortableTh label="商品ID" sortKey="id" currentSort={sort} onChange={setSort} style={{ width: 130 }} className="center" />
                  <SortableTh label="價格" sortKey="price" currentSort={sort} onChange={setSort} style={{ width: 130 }} className="right" />
                  <SortableTh label="更新時間" sortKey="stock_time" currentSort={sort} onChange={setSort} style={{ width: 180 }} />
                  <th style={{ width: 130 }} className="center">操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map(p => (
                  <tr key={p.product_id}>
                    <td>
                      <a href={p.url} target="_blank" rel="noreferrer" className="link product-name" title={p.name}>{p.name}</a>
                      <span className="tag" style={{ background: "#fef3c7", color: "#92400e", marginLeft: 6 }}>少量</span>
                    </td>
                    <td className="mono small center">{p.product_id}</td>
                    <td className="mono right">{p.price ? `NT$${Math.round(p.price).toLocaleString()}` : "—"}</td>
                    <td className="mono small muted">{p.stock_updated_at}</td>
                    <td className="center">
                      <AddButton productId={p.product_id} addingId={addingId} addedIds={addedIds} onAdd={handleAdd} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}



function SearchBox({ value, onChange, placeholder }) {
  const [input, setInput] = useState("");
  const submit = () => {
    onChange(input);
    setInput("");
  };
  return (
    <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
      <input
        type="text"
        value={input}
        onChange={e => setInput(e.target.value)}
        onKeyDown={e => e.key === "Enter" && submit()}
        placeholder={placeholder || "搜尋商品名稱或 ID..."}
        className="search-input"
        style={{ flex: 1 }}
      />
      <button onClick={submit} className="btn-primary">搜尋</button>
      {value && (
        <button onClick={() => onChange("")} className="btn-secondary">清除</button>
      )}
    </div>
  );
}

function Pagination({ page, totalPages, onPageChange }) {
  const tp = totalPages || 1;
  return (
    <div style={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 12, padding: 16, borderTop: "1px solid #f3f4f6" }}>
      <button onClick={() => onPageChange(1)} disabled={page === 1} className="btn-secondary">« 首頁</button>
      <button onClick={() => onPageChange(page - 1)} disabled={page === 1} className="btn-secondary">‹ 上一頁</button>
      <span style={{ fontSize: 14, color: "#374151", minWidth: 100, textAlign: "center" }}>第 {page} / {tp} 頁</span>
      <button onClick={() => onPageChange(page + 1)} disabled={page >= tp} className="btn-secondary">下一頁 ›</button>
      <button onClick={() => onPageChange(tp)} disabled={page >= tp} className="btn-secondary">末頁 »</button>
    </div>
  );
}

function PriceChanges({ type, title }) {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const { addingId, addedIds, message, handleAdd } = useAddToList();
  const PAGE_SIZE = 50;

  useEffect(() => {
    setLoading(true);
    authFetch(`/api/catalog/price-changes?type=${type}&days=0&page=${page}&page_size=${PAGE_SIZE}&q=${encodeURIComponent(q)}`)
      .then(r => r.json())
      .then(d => {
        setItems(d.items || []);
        setTotalPages(d.total_pages || 1);
        setTotal(d.total || 0);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [type, page, q]);

  useEffect(() => { setPage(1); }, [type, q]);

  const isDown = type === "down";

  return (
    <div className="page-fill">
      <h1 className="page-title">{title}</h1>
      <SearchBox value={q} onChange={setQ} />
      <MessageToast message={message} />

      {loading && <div className="loading">載入中...</div>}
      {!loading && items.length === 0 && (
        <div className="empty-state">目前無{isDown ? "降價" : "漲價"}商品{q && `（含「${q}」）`}</div>
      )}

      {!loading && items.length > 0 && (
        <div className="card card-fill">
          <div className="card-header">
            <span>共 {total.toLocaleString()} 件{isDown ? "降價" : "漲價"}商品</span>
          </div>
          <div className="table-scroll">
            <table className="table">
              <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                <tr>
                  <th>商品名稱</th>
                  <th style={{ width: 130 }} className="center">商品ID</th>
                  <th style={{ width: 110 }} className="right">{isDown ? "原價" : "原特價"}</th>
                  <th style={{ width: 110 }} className="right">{isDown ? "折扣" : "漲價"}</th>
                  <th style={{ width: 110 }} className="right">售價</th>
                  <th style={{ width: 160 }}>{isDown ? "降價時間" : "漲價時間"}</th>
                  <th style={{ width: 130 }} className="center">操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map(p => {
                  const diff = Math.abs(p.old_price - p.new_price);
                  return (
                    <tr key={p.url || p.product_id}>
                      <td>
                        <a href={p.url} target="_blank" rel="noreferrer" className="link product-name" title={p.name}>{p.name}</a>
                      </td>
                      <td className="mono small center">{p.product_id}</td>
                      <td className="mono right">NT${Math.round(p.old_price).toLocaleString()}</td>
                      <td className="mono right" style={{ color: isDown ? "#16a34a" : "#dc2626" }}>
                        {isDown ? "−" : "+"}NT${Math.round(diff).toLocaleString()}
                      </td>
                      <td className="mono right" style={{ fontWeight: 600, color: "#dc2626" }}>
                        NT${Math.round(p.new_price).toLocaleString()}
                      </td>
                      <td className="mono small muted">{p.checked_at}</td>
                      <td className="center">
                        <AddButton productId={p.product_id} addingId={addingId} addedIds={addedIds} onAdd={handleAdd} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}

// ===== 最新補貨（最新完售用 OutOfStockProducts 不需新元件） =====
function StockChanges({ type, title }) {
  const [items, setItems] = useState([]);
  const [days, setDays] = useState(1);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const { addingId, addedIds, message, handleAdd } = useAddToList();
  const PAGE_SIZE = 50;

  useEffect(() => {
    setLoading(true);
    authFetch(`/api/catalog/stock-changes?type=${type}&days=${days}&page=${page}&page_size=${PAGE_SIZE}&q=${encodeURIComponent(q)}`)
      .then(r => r.json())
      .then(d => {
        setItems(d.items || []);
        setTotalPages(d.total_pages || 1);
        setTotal(d.total || 0);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [type, days, page, q]);

  useEffect(() => { setPage(1); }, [type, days, q]);

  return (
    <div className="page-fill">
      <h1 className="page-title">{title}</h1>
      <SearchBox value={q} onChange={setQ} />
      <DayFilter days={days} setDays={setDays} />
      <MessageToast message={message} />

      {loading && <div className="loading">載入中...</div>}
      {!loading && items.length === 0 && (
        <div className="empty-state">最近 {days} 天無相關商品</div>
      )}

      {!loading && items.length > 0 && (
        <div className="card card-fill">
          <div className="card-header">
            <span>共 {total.toLocaleString()} 件</span>
          </div>
          <div className="table-scroll">
            <table className="table">
              <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                <tr>
                  <th>商品名稱</th>
                  <th style={{ width: 130 }} className="center">商品ID</th>
                  <th style={{ width: 110 }} className="right">價格</th>
                  <th style={{ width: 160 }}>{type === "in" ? "上架時間" : "完售時間"}</th>
                  <th style={{ width: 130 }} className="center">操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map(p => (
                  <tr key={p.url}>
                    <td>
                      <a href={p.url} target="_blank" rel="noreferrer" className="link product-name" title={p.name}>{p.name}</a>
                    </td>
                    <td className="mono small center">{p.product_id}</td>
                    <td className="mono right">{p.price ? `NT$${Math.round(p.price).toLocaleString()}` : "—"}</td>
                    <td className="mono small muted">{p.checked_at}</td>
                    <td className="center">
                      <AddButton productId={p.product_id} addingId={addingId} addedIds={addedIds} onAdd={handleAdd} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}

// ===== 出清商品 =====
function Clearance() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const { addingId, addedIds, message, handleAdd } = useAddToList();
  const PAGE_SIZE = 50;

  useEffect(() => {
    setLoading(true);
    authFetch(`/api/catalog/clearance-now?page=${page}&page_size=${PAGE_SIZE}&q=${encodeURIComponent(q)}`)
      .then(r => r.json())
      .then(d => {
        setItems(d.items || []);
        setTotalPages(d.total_pages || 1);
        setTotal(d.total || 0);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [page, q]);

  useEffect(() => { setPage(1); }, [q]);

  return (
    <div className="page-fill">
      <h1 className="page-title">🏷️ 出清商品</h1>
      <SearchBox value={q} onChange={setQ} />
      <MessageToast message={message} />

      {loading && <div className="loading">載入中...</div>}
      {!loading && items.length === 0 && (
        <div className="empty-state">目前無出清商品{q && `（含「${q}」）`}</div>
      )}

      {!loading && items.length > 0 && (
        <div className="card card-fill">
          <div className="card-header"><span>共 {total.toLocaleString()} 件出清商品（價格尾數 7）</span></div>
          <div className="table-scroll">
            <table className="table">
              <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                <tr>
                  <th>商品名稱</th>
                  <th style={{ width: 140 }} className="center">商品ID</th>
                  <th style={{ width: 110 }} className="right">原價</th>
                  <th style={{ width: 110 }} className="right">出清價</th>
                  <th style={{ width: 130 }} className="center">操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map(p => (
                  <tr key={p.product_id}>
                    <td>
                      <a href={p.url} target="_blank" rel="noreferrer" className="link product-name" title={p.name}>{p.name}</a>
                    </td>
                    <td className="mono small center">{p.product_id}</td>
                    <td className="mono right">{p.base_price ? `NT$${Math.round(p.base_price).toLocaleString()}` : "—"}</td>
                    <td className="mono right" style={{ fontWeight: 600, color: "#dc2626" }}>NT${Math.round(p.price).toLocaleString()}</td>
                    <td className="center">
                      <AddButton productId={p.product_id} addingId={addingId} addedIds={addedIds} onAdd={handleAdd} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}

function RemovedProducts() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("");
  const [q, setQ] = useState("");
  const PAGE_SIZE = 50;

  useEffect(() => {
    setLoading(true);
    authFetch(`/api/catalog/removed?days=0&page=${page}&page_size=${PAGE_SIZE}&sort=${sort}&q=${encodeURIComponent(q)}&no_tire=1`)
      .then(r => r.json())
      .then(d => {
        setItems(d.items || []);
        setTotalPages(d.total_pages || 1);
        setTotal(d.total || 0);
      })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [page, sort, q]);

  useEffect(() => { setPage(1); }, [sort, q]);

  return (
    <div className="page-fill">
      <h1 className="page-title">🗑️ 最新下架</h1>
      <SearchBox value={q} onChange={setQ} />

      {loading && <div className="loading">載入中...</div>}
      {!loading && items.length === 0 && (
        <div className="empty-state">目前無下架商品{q && `（含「${q}」）`}</div>
      )}

      {!loading && items.length > 0 && (
        <div className="card card-fill">
          <div className="card-header">
            <span>共 {total.toLocaleString()} 件下架商品</span>
          </div>
          <div className="table-scroll">
            <table className="table">
              <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                <tr>
                  <SortableTh label="商品名稱" sortKey="name" currentSort={sort} onChange={setSort} />
                  <SortableTh label="商品ID" sortKey="id" currentSort={sort} onChange={setSort} style={{ width: 130 }} className="center" />
                  <SortableTh label="價格" sortKey="price" currentSort={sort} onChange={setSort} style={{ width: 130 }} className="right" />
                  <SortableTh label="下架時間" sortKey="last_seen" currentSort={sort} onChange={setSort} style={{ width: 160 }} />
                </tr>
              </thead>
              <tbody>
                {items.map(p => (
                  <tr key={p.product_id}>
                    <td>
                      <a href={p.url} target="_blank" rel="noreferrer" className="link product-name" title={p.name}>{p.name}</a>
                    </td>
                    <td className="mono small center">{p.base_id}</td>
                    <td className="mono right">{p.price ? `NT$${p.price.toLocaleString()}` : "—"}</td>
                    <td className="mono small muted">{p.last_seen}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}

function SortableTh({ label, sortKey, currentSort, onChange, ...rest }) {
  const isAsc = currentSort === `${sortKey}_asc`;
  const isDesc = currentSort === `${sortKey}_desc`;
  const next = isAsc ? `${sortKey}_desc` : isDesc ? "" : `${sortKey}_asc`;
  const arrow = isAsc ? "↑" : isDesc ? "↓" : "⇅";
  return (
    <th {...rest} className={`${rest.className || ""} sortable`} onClick={() => onChange(next)}>
      {label} {arrow}
    </th>
  );
}

function StockBadge({ status, added_at, product_status, showNew = true }) {
  // 判斷是否為 7 天內新品
  const isNew = showNew && added_at && (new Date() - new Date(added_at.replace(" ", "T"))) < 7 * 24 * 60 * 60 * 1000;
  if (isNew) return <span className="badge blue">新上架</span>;
  if (product_status === "removed") return <span className="badge red">已下架</span>;
  if (status === "inStock") return <span className="badge green">有貨</span>;
  if (status === "outOfStock") return <span className="badge red">缺貨</span>;
  if (status === "lowStock") return <span className="badge yellow">少量</span>;
  return <span className="muted small">—</span>;
}

function OutOfStockProducts() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sort, setSort] = useState("");
  const [q, setQ] = useState("");
  const { addingId, addedIds, message, handleAdd } = useAddToList();
  const PAGE_SIZE = 50;

  useEffect(() => {
    setLoading(true);
    authFetch(`/api/catalog/out-of-stock?page=${page}&page_size=${PAGE_SIZE}&sort=${sort}&q=${encodeURIComponent(q)}&no_tire=1`)
      .then(r => r.json())
      .then(d => {
        setItems(d.items || []);
        setTotalPages(d.total_pages || 1);
        setTotal(d.total || 0);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [page, sort, q]);

  useEffect(() => { setPage(1); }, [sort, q]);

  return (
    <div className="page-fill">
      <h1 className="page-title">📭 最新完售</h1>
      <SearchBox value={q} onChange={setQ} />
      <MessageToast message={message} />

      {loading && <div className="loading">載入中...</div>}
      {!loading && items.length === 0 && (
        <div className="empty-state">目前無完售商品{q && `（含「${q}」）`}</div>
      )}

      {!loading && items.length > 0 && (
        <div className="card card-fill">
          <div className="card-header">
            <span>共 {total.toLocaleString()} 件完售商品</span>
          </div>
          <div className="table-scroll">
            <table className="table">
              <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                <tr>
                  <SortableTh label="商品名稱" sortKey="name" currentSort={sort} onChange={setSort} />
                  <SortableTh label="商品ID" sortKey="id" currentSort={sort} onChange={setSort} style={{ width: 130 }} className="center" />
                  <SortableTh label="價格" sortKey="price" currentSort={sort} onChange={setSort} style={{ width: 130 }} className="right" />
                  <SortableTh label="完售時間" sortKey="stock_time" currentSort={sort} onChange={setSort} style={{ width: 180 }} />
                  <th style={{ width: 130 }} className="center">操作</th>
                </tr>
              </thead>
              <tbody>
                {items.map(p => (
                  <tr key={p.product_id}>
                    <td>
                      <a href={p.url} target="_blank" rel="noreferrer" className="link product-name" title={p.name}>{p.name}</a>
                      <span className="tag" style={{ background: "#fee2e2", color: "#991b1b", marginLeft: 6 }}>缺貨</span>
                    </td>
                    <td className="mono small center">{p.product_id}</td>
                    <td className="mono right">{p.price ? `NT$${Math.round(p.price).toLocaleString()}` : "—"}</td>
                    <td className="mono small muted">{p.stock_updated_at}</td>
                    <td className="center">
                      <AddButton productId={p.product_id} addingId={addingId} addedIds={addedIds} onAdd={handleAdd} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
        </div>
      )}
    </div>
  );
}

export default App;
